import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import API from "../api/api";
import RAG_API from "../api/ragApi";
import TypingText from "./TypingText";
import "../style/chatwindow.css";


import {
  setActiveChatId, setMessages, addMessage,
  setLoading, migrateChatId, addChatToList,
  toggleSidebarCollapsed,
  updateMessageVersion, appendMessageVersion,
} from "../store/chatSlice";
import { setCredits } from "../store/userSlice";
import {
  GuestAuthPopup, WelcomeCard, GuestLimitPopup, CreditsExhaustedPopup, SystemNoticePopup
} from "./Popups";
import ShareModal from "./ShareModal";
import { getSystemNotice } from "../api/ragApi";
import { useTransliteratedName, getTimeBasedGreeting } from "../utils/transliterate";
import { LANGUAGES } from "../utils/language";
import SourcesSidebar, { SourcePillButton } from "./SourcesSidebar";
import { MAX_CHAT_INPUT_LENGTH } from "../constants/chatConstants";

// ─── Constants ────────────────────────────────────────────────────────────────
const GUEST_FREE_CHATS = 5;

function StarburstIcon({ size = 32, color = "#d97706", className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
      <path d="M12 2v20M2 12h20M4.93 4.93l14.14 14.14M4.93 19.07L19.07 4.93" stroke={color} strokeWidth="2.4" strokeLinecap="round" />
    </svg>
  );
}

function UiIcon({ name, className = "" }) {
  const classes = className ? `ui-icon ${className}` : "ui-icon";

  switch (name) {
    case "language":
      return (
        <svg viewBox="0 0 24 24" className={classes} aria-hidden="true">
          <path d="M4 5h9M8.5 5c0 6-2.5 10-5.5 12M10 17c-2-1.5-3.5-3.2-4.5-5M14 6h6M17 4v2M14.5 19l2.5-7 2.5 7M15.3 16.8h3.4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "state":
      return (
        <svg viewBox="0 0 24 24" className={classes} aria-hidden="true">
          <path d="M12 21s6-4.35 6-10a6 6 0 1 0-12 0c0 5.65 6 10 6 10Z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          <circle cx="12" cy="11" r="2.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
        </svg>
      );
    case "department":
      return (
        <svg viewBox="0 0 24 24" className={classes} aria-hidden="true">
          <path d="M3 21h18M5 21V9l7-4 7 4v12M9 21v-5h6v5M8 12h.01M12 12h.01M16 12h.01" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "list":
      return (
        <svg viewBox="0 0 24 24" className={classes} aria-hidden="true">
          <path d="M8 7h10M8 12h10M8 17h10M4 7h.01M4 12h.01M4 17h.01" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "currency":
      return (
        <svg viewBox="0 0 24 24" className={classes} aria-hidden="true">
          <path d="M7 6h10M7 10h10M8 6c2 2 4 4.5 6 6.5C12 14.5 10 16 8 18h9" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "construction":
      return (
        <svg viewBox="0 0 24 24" className={classes} aria-hidden="true">
          <path d="M4 20h16M6 20V9l6-4 6 4v11M9 12h6M12 9v6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "leaf":
      return (
        <svg viewBox="0 0 24 24" className={classes} aria-hidden="true">
          <path d="M6 13c0-5 4-8 12-8 0 8-3 12-8 12-2.5 0-4-1.5-4-4Z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M8 16c2-2 5-4 9-6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      );
    case "book":
      return (
        <svg viewBox="0 0 24 24" className={classes} aria-hidden="true">
          <path d="M5 6.5A2.5 2.5 0 0 1 7.5 4H19v15H7.5A2.5 2.5 0 0 0 5 21V6.5ZM5 6.5V19" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "graduation":
      return (
        <svg viewBox="0 0 24 24" className={classes} aria-hidden="true">
          <path d="m3 9 9-4 9 4-9 4-9-4Zm3 2.5v4L12 19l6-3.5v-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "health":
      return (
        <svg viewBox="0 0 24 24" className={classes} aria-hidden="true">
          <path d="M12 5v14M5 12h14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <rect x="4" y="4" width="16" height="16" rx="3" fill="none" stroke="currentColor" strokeWidth="1.8" />
        </svg>
      );
    case "shield":
      return (
        <svg viewBox="0 0 24 24" className={classes} aria-hidden="true">
          <path d="M12 3 6 5.5V11c0 4.5 2.8 7.6 6 10 3.2-2.4 6-5.5 6-10V5.5L12 3Z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "tree":
      return (
        <svg viewBox="0 0 24 24" className={classes} aria-hidden="true">
          <path d="M12 20v-5M7 11a5 5 0 1 1 10 0c0 2.5-2 4-5 4s-5-1.5-5-4Z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "city":
      return (
        <svg viewBox="0 0 24 24" className={classes} aria-hidden="true">
          <path d="M4 20V9l5-3v14M9 20h11V4l-6 2v14M13 9h.01M13 13h.01M17 9h.01M17 13h.01" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "village":
      return (
        <svg viewBox="0 0 24 24" className={classes} aria-hidden="true">
          <path d="m3 12 6-5 6 5M5 20v-6h8v6M15 20V9l4-3 2 2v12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "droplet":
      return (
        <svg viewBox="0 0 24 24" className={classes} aria-hidden="true">
          <path d="M12 3s5 5.3 5 9a5 5 0 1 1-10 0c0-3.7 5-9 5-9Z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "bolt":
      return (
        <svg viewBox="0 0 24 24" className={classes} aria-hidden="true">
          <path d="M13 2 6 13h5l-1 9 8-12h-5l0-8Z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "factory":
      return (
        <svg viewBox="0 0 24 24" className={classes} aria-hidden="true">
          <path d="M3 20V9l6 3V9l6 3V5h6v15H3Z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "workers":
      return (
        <svg viewBox="0 0 24 24" className={classes} aria-hidden="true">
          <path d="M7 20v-2a3 3 0 0 1 3-3h4a3 3 0 0 1 3 3v2M9 8a3 3 0 1 0 6 0M8 7h8l-1-2H9L8 7Z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "scales":
      return (
        <svg viewBox="0 0 24 24" className={classes} aria-hidden="true">
          <path d="M12 4v16M7 7h10M9 7 6 12h6L9 7Zm6 0-3 5h6l-3-5Z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "users":
      return (
        <svg viewBox="0 0 24 24" className={classes} aria-hidden="true">
          <path d="M7 20v-1a4 4 0 0 1 4-4h2a4 4 0 0 1 4 4v1M9 9a3 3 0 1 0 6 0" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "compass":
      return (
        <svg viewBox="0 0 24 24" className={classes} aria-hidden="true">
          <circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" strokeWidth="1.8" />
          <path d="m15 9-5 2-2 5 5-2 2-5Z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "medal":
      return (
        <svg viewBox="0 0 24 24" className={classes} aria-hidden="true">
          <circle cx="12" cy="14" r="4" fill="none" stroke="currentColor" strokeWidth="1.8" />
          <path d="M9 3h6l-1 6h-4L9 3Z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "theatre":
      return (
        <svg viewBox="0 0 24 24" className={classes} aria-hidden="true">
          <path d="M5 6c2 1 4 1 7 0s5-1 7 0v9c-2-1-4-1-7 0s-5 1-7 0V6Zm4 4h.01M15 4v16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "home":
      return (
        <svg viewBox="0 0 24 24" className={classes} aria-hidden="true">
          <path d="m4 11 8-6 8 6M6 10.5V20h12v-9.5M10 20v-5h4v5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "bus":
      return (
        <svg viewBox="0 0 24 24" className={classes} aria-hidden="true">
          <path d="M7 18h10M6 16V8a3 3 0 0 1 3-3h6a3 3 0 0 1 3 3v8M8 20a1 1 0 1 0 0-.01M16 20a1 1 0 1 0 0-.01M6 11h12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "anchor":
      return (
        <svg viewBox="0 0 24 24" className={classes} aria-hidden="true">
          <path d="M12 4a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3ZM12 7v11M7 14a5 5 0 0 0 10 0M7 18h10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "cart":
      return (
        <svg viewBox="0 0 24 24" className={classes} aria-hidden="true">
          <path d="M4 6h2l2 9h9l2-6H7M10 20a1 1 0 1 0 0-.01M17 20a1 1 0 1 0 0-.01" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "handshake":
      return (
        <svg viewBox="0 0 24 24" className={classes} aria-hidden="true">
          <path d="m7 12 3 3 2-2 2 2 3-3M4 10l3 2 4-4h2l4 4 3-2" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "laptop":
      return (
        <svg viewBox="0 0 24 24" className={classes} aria-hidden="true">
          <path d="M5 7h14v9H5V7Zm-2 11h18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "megaphone":
      return (
        <svg viewBox="0 0 24 24" className={classes} aria-hidden="true">
          <path d="M4 13V9l10-4v12L4 13Zm10-2h3a3 3 0 0 0 0-6h-3M6 13l1 5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "tools":
      return (
        <svg viewBox="0 0 24 24" className={classes} aria-hidden="true">
          <path d="m14 6 4 4M4 20l8-8M10 4a4 4 0 0 0 5 5l-9 9-3 1 1-3 9-9A4 4 0 0 0 10 4Z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "alert":
      return (
        <svg viewBox="0 0 24 24" className={classes} aria-hidden="true">
          <path d="M12 9v4M12 17h.01M10.3 4.6 3.8 16a2 2 0 0 0 1.7 3h13a2 2 0 0 0 1.7-3L13.7 4.6a2 2 0 0 0-3.4 0Z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "cow":
      return (
        <svg viewBox="0 0 24 24" className={classes} aria-hidden="true">
          <path d="M6 8 4 6M18 8l2-2M7 18v-3a5 5 0 0 1 10 0v3M8 10h8M9 13h.01M15 13h.01" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "fish":
      return (
        <svg viewBox="0 0 24 24" className={classes} aria-hidden="true">
          <path d="M4 12s3-5 9-5c4 0 7 5 7 5s-3 5-7 5c-6 0-9-5-9-5Zm6 0h.01" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "pickaxe":
      return (
        <svg viewBox="0 0 24 24" className={classes} aria-hidden="true">
          <path d="M13 5c3 0 5 1 7 3-3 1-5 1-7 0l-7 11M10 4l4 4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "stethoscope":
      return (
        <svg viewBox="0 0 24 24" className={classes} aria-hidden="true">
          <path d="M8 4v5a4 4 0 0 0 8 0V4M12 13v3a3 3 0 0 0 6 0v-2" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          <circle cx="18" cy="13" r="1.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
        </svg>
      );
    case "plane":
      return (
        <svg viewBox="0 0 24 24" className={classes} aria-hidden="true">
          <path d="m3 13 18-9-4 16-4-5-4 2 1-5-7 1Z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "search":
      return (
        <svg viewBox="0 0 24 24" className={classes} aria-hidden="true">
          <circle cx="11" cy="11" r="6" fill="none" stroke="currentColor" strokeWidth="1.8" />
          <path d="m20 20-4.35-4.35" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      );
    case "available":
      return (
        <svg viewBox="0 0 24 24" className={classes} aria-hidden="true">
          <circle cx="12" cy="12" r="8" fill="currentColor" />
        </svg>
      );
    case "soon":
      return (
        <svg viewBox="0 0 24 24" className={classes} aria-hidden="true">
          <circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" strokeWidth="1.8" />
          <path d="M12 8v4l2.5 1.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    default:
      return null;
  }
}

const getDepartmentIconName = (code) => {
  const map = {
    ALL: "department",
    GAD: "list",
    FIN: "currency",
    PWD: "construction",
    AGR: "leaf",
    EDU: "book",
    HED: "graduation",
    HLT: "health",
    HOM: "shield",
    REV: "tree",
    URB: "city",
    RDD: "village",
    WAT: "droplet",
    ENE: "bolt",
    IND: "factory",
    LAB: "workers",
    SOC: "scales",
    TRB: "users",
    WCD: "users",
    ENV: "leaf",
    TOU: "compass",
    SPT: "medal",
    CUL: "theatre",
    HOU: "home",
    TRN: "bus",
    MRT: "anchor",
    FOD: "cart",
    COP: "handshake",
    LAW: "scales",
    PAR: "department",
    IT: "laptop",
    PR: "megaphone",
    SKL: "tools",
    DIS: "alert",
    ANH: "cow",
    FSH: "fish",
    MIN: "pickaxe",
    MED: "stethoscope",
    NRI: "plane",
  };

  return map[code] || "department";
};

// ─── Language Config ──────────────────────────────────────────────────────────
// LANGUAGES is imported from "../utils/language"



// ─── GR Departments ───────────────────────────────────────────────────────────
const GR_DEPARTMENTS = [
  { code: "ALL", label: "All Departments", icon: "🏛️" },
  { code: "GAD", label: "General Administration", icon: "📋" },
  { code: "FIN", label: "Finance", icon: "💰" },
  { code: "PWD", label: "Public Works", icon: "🏗️" },
  { code: "AGR", label: "Agriculture", icon: "🌾" },
  { code: "EDU", label: "School Education & Sports", icon: "📚" },
  { code: "HED", label: "Higher & Technical Education", icon: "🎓" },
  { code: "HLT", label: "Public Health", icon: "🏥" },
  { code: "HOM", label: "Home (Police)", icon: "🛡️" },
  { code: "REV", label: "Revenue & Forest", icon: "🌲" },
  { code: "URB", label: "Urban Development", icon: "🏙️" },
  { code: "RDD", label: "Rural Development", icon: "🏡" },
  { code: "WAT", label: "Water Resources", icon: "💧" },
  { code: "WSS", label: "Water Supply & Sanitation", icon: "🚰" },
  { code: "SWC", label: "Soil & Water Conservation", icon: "🌱" },
  { code: "IND", label: "Industries, Energy & Labour", icon: "🏭" },
  { code: "SOC", label: "Social Justice", icon: "👥" },
  { code: "TRB", label: "Tribal Development", icon: "🏹" },
  { code: "MND", label: "Minority Development", icon: "🤝" },
  { code: "DIV", label: "Divyang Kalyan (Welfare)", icon: "♿" },
  { code: "WCD", label: "Women & Child Development", icon: "👩‍👧" },
  { code: "ENV", label: "Environment", icon: "🌿" },
  { code: "TOU", label: "Tourism & Cultural Affairs", icon: "🎭" },
  { code: "HOU", label: "Housing", icon: "🏠" },
  { code: "FOD", label: "Food & Civil Supplies", icon: "🛒" },
  { code: "COP", label: "Co-operation & Textiles", icon: "🧵" },
  { code: "LAW", label: "Law & Judiciary", icon: "⚖️" },
  { code: "PAR", label: "Parliamentary Affairs", icon: "🏛️" },
  { code: "IT", label: "Information Technology", icon: "💻" },
  { code: "SKL", label: "Skill Development", icon: "🛠️" },
  { code: "PLN", label: "Planning", icon: "📊" },
  { code: "MAR", label: "Marathi Language", icon: "📖" },
  { code: "MED", label: "Medical Education & Drugs", icon: "💊" },
  { code: "TRN", label: "Transport", icon: "🚌" },
];

const WELCOME_BY_LANG = {
  mr: "नमस्कार! मी GR/Tender AI Assistant आहे. कृपया तुमचा प्रश्न विचारा.",
  hi: "नमस्ते! मैं GR/Tender AI Assistant हूँ। कृपया अपना प्रश्न पूछें।",
  en: "Hello! I'm the GR/Tender AI Assistant. Please ask your question.",
  bn: "নমস্কার! আমি GR/Tender AI Assistant। আপনার প্রশ্ন জিজ্ঞাসা করুন।",
  te: "నమస్కారం! నేను GR/Tender AI Assistant ని. మీ ప్రశ్న అడగండి.",
  ta: "வணக்கம்! நான் GR/Tender AI Assistant. உங்கள் கேள்வியை கேளுங்கள்.",
  gu: "નમસ્તે! હું GR/Tender AI Assistant છું. તમારો પ્રશ્ન પૂછો.",
  kn: "ನಮಸ್ಕಾರ! ನಾನು GR/Tender AI Assistant. ನಿಮ್ಮ ಪ್ರಶ್ನೆ ಕೇಳಿ.",
  ml: "നമസ്കാരം! ഞാൻ GR/Tender AI Assistant ആണ്. നിങ്ങളുടെ ചോദ്യം ചോദിക്കൂ.",
  pa: "ਸਤਿ ਸ੍ਰੀ ਅਕਾਲ! ਮੈਂ GR/Tender AI Assistant ਹਾਂ। ਆਪਣਾ ਸਵਾਲ ਪੁੱਛੋ।",
  ur: "السلام علیکم! میں GR/Tender AI Assistant ہوں۔ اپنا سوال پوچھیں۔",
  or: "ନମସ୍କାର! ମୁଁ GR/Tender AI Assistant। ଆପଣଙ୍କ ପ୍ରଶ୍ନ ପଚାରନ୍ତୁ।",
};

const UI_TEXT = {
  en: {
    department: "Department",
    allDepartments: "All Departments",
    filterByDepartment: "Filter by Department",
    searchDepartment: "Search department...",
    noDepartmentFound: 'No department found for "{search}"',
    clear: "Clear",
    chooseState: "Choose State",
    selectState: "Select State",
    searchState: "Search state...",
    availableNow: "Available Now",
    comingSoon: "Coming Soon",
    soon: "Soon",
    noStateFound: 'No state found for "{search}"',
    summary: "Summary",
    viewSources: "View Sources",
    openGrPdf: "Open GR PDF",
    govAssistantTitle: "Government Assistant",
    govAssistantSub: "Maharashtra Government Digital Communication",
    chatEmptyTitle: "How can I help you?",
  },
  hi: {
    department: "विभाग",
    allDepartments: "सभी विभाग",
    filterByDepartment: "विभाग के अनुसार फ़िल्टर करें",
    searchDepartment: "विभाग खोजें...",
    noDepartmentFound: '"{search}" के लिए कोई विभाग नहीं मिला',
    clear: "साफ करें",
    chooseState: "राज्य चुनें",
    selectState: "राज्य चुनें",
    searchState: "राज्य खोजें...",
    availableNow: "अभी उपलब्ध",
    comingSoon: "जल्द आ रहा है",
    soon: "जल्द",
    noStateFound: '"{search}" के लिए कोई राज्य नहीं मिला',
    summary: "सारांश",
    viewSources: "स्रोत देखें",
    openGrPdf: "GR PDF खोलें",
    govAssistantTitle: "शासन सहायक",
    govAssistantSub: "महाराष्ट्र शासन डिजिटल संवाद",
    chatEmptyTitle: "मैं आपकी कैसे मदद कर सकता हूँ?",
  },
  mr: {
    department: "विभाग",
    allDepartments: "सर्व विभाग",
    filterByDepartment: "विभागानुसार फिल्टर करा",
    searchDepartment: "विभाग शोधा...",
    noDepartmentFound: '"{search}" साठी विभाग सापडला नाही',
    clear: "साफ करा",
    chooseState: "राज्य निवडा",
    selectState: "राज्य निवडा",
    searchState: "राज्य शोधा...",
    availableNow: "आता उपलब्ध",
    comingSoon: "लवकरच",
    soon: "लवकरच",
    noStateFound: '"{search}" साठी राज्य सापडले नाही',
    summary: "सारांश",
    viewSources: "स्रोत पहा",
    openGrPdf: "GR PDF उघडा",
    govAssistantTitle: "शासन सहाय्यक",
    govAssistantSub: "महाराष्ट्र शासन डिजिटल संवाद",
    chatEmptyTitle: "मी तुमची कशी मदत करू?",
  },
  bn: {
    department: "বিভাগ",
    allDepartments: "সব বিভাগ",
    filterByDepartment: "বিভাগ অনুসারে ফিল্টার করুন",
    searchDepartment: "বিভাগ খুঁজুন...",
    noDepartmentFound: '"{search}" এর জন্য কোনো বিভাগ পাওয়া যায়নি',
    clear: "মুছে ফেলুন",
    chooseState: "রাজ্য নির্বাচন করুন",
    selectState: "রাজ্য নির্বাচন করুন",
    searchState: "রাজ্য খুঁজুন...",
    availableNow: "এখন উপলব্ধ",
    comingSoon: "শীঘ্রই আসছে",
    soon: "শীঘ্রই",
    noStateFound: '"{search}" এর জন্য কোনো রাজ্য পাওয়া যায়নি',
    summary: "সারসংক্ষেপ",
    viewSources: "উৎস দেখুন",
    openGrPdf: "GR PDF খুলুন",
    govAssistantTitle: "সরকারি সহায়ক",
    govAssistantSub: "মহারাষ্ট্র সরকার ডিজিটাল যোগাযোগ",
    chatEmptyTitle: "আমি কীভাবে আপনাকে সাহায্য করতে পারি?",
  },
  te: {
    department: "విభాగం",
    allDepartments: "అన్ని విభాగాలు",
    filterByDepartment: "విభాగం ఆధారంగా ఫిల్టర్ చేయండి",
    searchDepartment: "విభాగం కోసం వెతకండి...",
    noDepartmentFound: '"{search}" కోసం ఏ విభాగం కనుగొనబడలేదు',
    clear: "క్లియర్",
    chooseState: "రాష్ట్రం ఎంచుకోండి",
    selectState: "రాష్ట్రం ఎంచుకోండి",
    searchState: "రాష్ట్రం కోసం వెతకండి...",
    availableNow: "ఇప్పుడు అందుబాటులో ఉంది",
    comingSoon: "త్వరలో రానుంది",
    soon: "త్వరలో",
    noStateFound: '"{search}" కోసం ఏ రాష్ట్రం కనుగొనబడలేదు',
    summary: "సారాంశం",
    viewSources: "మూలాలు చూడండి",
    openGrPdf: "GR PDF తెరవండి",
    govAssistantTitle: "ప్రభుత్వ సహాయకుడు",
    govAssistantSub: "మహారాష్ట్ర ప్రభుత్వం డిజిటల్ కమ్యూనికేషన్",
    chatEmptyTitle: "నేను మీకు ఎలా సహాయం చేయగలను?",
  },
  ta: {
    department: "துறை",
    allDepartments: "அனைத்து துறைகள்",
    filterByDepartment: "துறை வாரியாக வடிகட்டவும்",
    searchDepartment: "துறையைத் தேடுங்கள்...",
    noDepartmentFound: '"{search}" க்கான துறை எதுவும் காணப்படவில்லை',
    clear: "அழி",
    chooseState: "மாநிலத்தைத் தேர்ந்தெடுக்கவும்",
    selectState: "மாநிலத்தைத் தேர்ந்தெடுக்கவும்",
    searchState: "மாநிலத்தைத் தேடுங்கள்...",
    availableNow: "இப்போது கிடைக்கிறது",
    comingSoon: "விரைவில்",
    soon: "விரைவில்",
    noStateFound: '"{search}" க்கான மாநிலம் எதுவும் காணப்படவில்லை',
    summary: "சுருக்கம்",
    viewSources: "ஆதாரங்களைப் பார்க்கவும்",
    openGrPdf: "GR PDF ஐத் திறக்கவும்",
    govAssistantTitle: "அரசு உதவியாளர்",
    govAssistantSub: "மகாராஷ்டிரா அரசு டிஜிட்டல் தொடர்பு",
    chatEmptyTitle: "நான் உங்களுக்கு எப்படி உதவ முடியும்?",
  },
  gu: {
    department: "વિભાગ",
    allDepartments: "બધા વિભાગો",
    filterByDepartment: "વિભાગ દ્વારા ફિલ્ટર કરો",
    searchDepartment: "વિભાગ શોધો...",
    noDepartmentFound: '"{search}" માટે કોઈ વિભાગ મળ્યો નથી',
    clear: "સાફ કરો",
    chooseState: "રાજ્ય પસંદ કરો",
    selectState: "રાજ્ય પસંદ કરો",
    searchState: "રાજ્ય શોધો...",
    availableNow: "હવે ઉપલબ્ધ",
    comingSoon: "ટૂંક સમયમાં આવી રહ્યું છે",
    soon: "ટૂંક સમયમાં",
    noStateFound: '"{search}" માટે કોઈ રાજ્ય મળ્યું નથી',
    summary: "સારાંશ",
    viewSources: "સ્ત્રોતો જુઓ",
    openGrPdf: "GR PDF ખોલો",
    govAssistantTitle: "સરકારી મદદનીશ",
    govAssistantSub: "મહારાષ્ટ્ર સરકાર ડિજિટલ સંચાર",
    chatEmptyTitle: "હું તમને કેવી રીતે મદદ કરી શકું?",
  },
  kn: {
    department: "ಇಲಾಖೆ",
    allDepartments: "ಎಲ್ಲಾ ಇಲಾಖೆಗಳು",
    filterByDepartment: "ಇಲಾಕೆಯ ಮೂಲಕ ಫಿಲ್ಟರ್ ಮಾಡಿ",
    searchDepartment: "ಇಲಾಖೆಯನ್ನು ಹುಡುಕಿ...",
    noDepartmentFound: '"{search}" ಗಾಗಿ ಯಾವುದೇ ಇಲಾಖೆ ಕಂಡುಬಂದಿಲ್ಲ',
    clear: "ತೆರವುಗೊಳಿಸಿ",
    chooseState: "ರಾಜ್ಯವನ್ನು ಆಯ್ಕೆ ಮಾಡಿ",
    selectState: "ರಾಜ್ಯವನ್ನು ಆಯ್ಕೆ ಮಾಡಿ",
    searchState: "ರಾಜ್ಯವನ್ನು ಹುಡುಕಿ...",
    availableNow: "ಈಗ ಲಭ್ಯವಿದೆ",
    comingSoon: "ಶೀಘ್ರದಲ್ಲೇ ಬರಲಿದೆ",
    soon: "ಶೀಘ್ರದಲ್ಲೇ",
    noStateFound: '"{search}" ಗಾಗಿ ಯಾವುದೇ राज्य ಕಂಡುಬಂದಿಲ್ಲ',
    summary: "ಸಾರಾಂಶ",
    viewSources: "ಮೂಲಗಳನ್ನು ವೀಕ್ಷಿಸಿ",
    openGrPdf: "GR PDF ತೆರೆಯಿರಿ",
    govAssistantTitle: "ಸರ್ಕಾರಿ ಸಹಾಯಕ",
    govAssistantSub: "ಮಹಾರಾಷ್ಟ್ರ ಸರ್ಕಾರ ಡಿಜಿಟಲ್ ಸಂವಹನ",
    chatEmptyTitle: "ನಾನು ನಿಮಗೆ ಹೇಗೆ ಸಹಾಯ ಮಾಡಲಿ?",
  },
  ml: {
    department: "വകുപ്പ്",
    allDepartments: "എല്ലാ വകുപ്പുകളും",
    filterByDepartment: "വകുപ്പ് അനുസരിച്ച് ഫിൽട്ടർ ചെയ്യുക",
    searchDepartment: "വകുപ്പ് തിരയുക...",
    noDepartmentFound: '"{search}" എന്നതിനായി വകുപ്പ് കണ്ടെത്താനായില്ല',
    clear: "വ്യത്തിയാക്കുക",
    chooseState: "സംസ്ഥാനം തിരഞ്ഞെടുക്കുക",
    selectState: "സംസ്ഥാനം തിരഞ്ഞെടുക്കുക",
    searchState: "സംസ്ഥാനം തിരയുക...",
    availableNow: "ഇപ്പോൾ ലഭ്യമാണ്",
    comingSoon: "ഉടൻ വരുന്നു",
    soon: "ഉടൻ",
    noStateFound: '"{search}" എന്നതിനായി സംസ്ഥാനം കണ്ടെത്താനായില്ല',
    summary: "സംഗ്രഹം",
    viewSources: "ഉറവിടങ്ങൾ കാണുക",
    openGrPdf: "GR PDF തുറക്കുക",
    govAssistantTitle: "സർക്കാർ സഹായി",
    govAssistantSub: "മഹാരാഷ്ട്ര സർക്കാർ ഡിജിറ്റൽ ആശയവിനിമയം",
    chatEmptyTitle: "ഞാൻ നിങ്ങളെ എങ്ങനെ സഹായിക്കും?",
  },
  pa: {
    department: "ਵਿਭਾਗ",
    allDepartments: "ਸਾਰੇ ਵਿਭਾਗ",
    filterByDepartment: "ਵਿਭਾਗ ਅਨੁਸਾਰ ਫਿਲਟਰ ਕਰੋ",
    searchDepartment: "ਵਿਭਾਗ ਖੋਜੋ...",
    noDepartmentFound: '"{search}" ਲਈ ਕੋਈ ਵਿਭਾਗ ਨਹੀਂ ਮਿਲਿਆ',
    clear: "ਸਾਫ਼ ਕਰੋ",
    chooseState: "ਰਾਜ ਚੁਣੋ",
    selectState: "ਰਾਜ ਚੁਣੋ",
    searchState: "ਰਾਜ ਖੋਜੋ...",
    availableNow: "ਹੁਣ ਉਪਲਬਧ",
    comingSoon: "ਜਲਦੀ ਆ ਰਿਹਾ ਹੈ",
    soon: "ਜਲਦੀ",
    noStateFound: '"{search}" ਲਈ ਕੋਈ ਰਾਜ ਨਹੀਂ ਮਿਲਿਆ',
    summary: "ਸੰਖੇਪ",
    viewSources: "ਸਰੋਤ ਦੇਖੋ",
    openGrPdf: "GR PDF ਖੋਲ੍ਹੋ",
    govAssistantTitle: "ਸਰਕਾਰੀ ਸਹਾਇਕ",
    govAssistantSub: "ਮਹਾਰਾਸ਼ਟਰ ਸਰਕਾਰ ਡਿਜੀਟਲ ਸੰਚਾਰ",
    chatEmptyTitle: "ਮੈਂ ਤੁਹਾਡੀ ਕਿਵੇਂ ਮਦਦ ਕਰ ਸਕਦਾ ਹਾਂ?",
  },
  ur: {
    department: "محکمہ",
    allDepartments: "تمام محکمے",
    filterByDepartment: "محکمہ کے لحاظ سے فلٹر کریں",
    searchDepartment: "محکمہ تلاش کریں...",
    noDepartmentFound: '"{search}" کے لیے کوئی محکمہ نہیں ملا',
    clear: "صاف کریں",
    chooseState: "ریاست منتخب کریں",
    selectState: "ریاست منتخب کریں",
    searchState: "ریاست تلاش کریں...",
    availableNow: "ابھی دستیاب ہے",
    comingSoon: "جلد آ رہا ہے",
    soon: "جلد",
    noStateFound: '"{search}" کے لیے کوئی ریاست نہیں ملی',
    summary: "خلاصہ",
    viewSources: "ذرائع دیکھیں",
    openGrPdf: "GR PDF کھولیں",
    govAssistantTitle: "سرکاری معاون",
    govAssistantSub: "مہاراشٹر حکومت ڈیجیٹل مواصلات",
    chatEmptyTitle: "میں آپ کی کیا مدد کر سکتا ہوں؟",
  },
  or: {
    department: "ବିଭାଗ",
    allDepartments: "ସମସ୍ତ ବିଭାଗ",
    filterByDepartment: "ବିଭାଗ ଅନୁଯାୟୀ ଫିଲ୍ଟର୍ କରନ୍ତୁ",
    searchDepartment: "ବିଭାଗ ସନ୍ଧାନ କରନ୍ତୁ...",
    noDepartmentFound: '"{search}" ପାଇଁ କୌଣସି ବିଭାଗ ମିଳିଲା ନାହିଁ',
    clear: "ସଫା କରନ୍ତୁ",
    chooseState: "ରାଜ୍ୟ ଚୟନ କରନ୍ତୁ",
    selectState: "ରାଜ୍ୟ ଚୟନ କരନ୍ତୁ",
    searchState: "ରାଜ୍ୟ ସନ୍ଧାନ କରନ୍ତୁ...",
    availableNow: "ଏବେ ଉପଲବ୍ଧ",
    comingSoon: "ଶୀଘ୍ର ଆସୁଛି",
    soon: "ଶୀଘ୍ର",
    noStateFound: '"{search}" ପାଇଁ କୌଣସି ରାଜ୍ୟ ମିଳିଲା ନାହିଁ',
    summary: "ସାରାଂଶ",
    viewSources: "ଉତ୍ସ ଦେଖନ୍ତୁ",
    openGrPdf: "GR PDF ଖୋଲନ୍ତୁ",
    govAssistantTitle: "ସରକାରୀ ସହାୟକ",
    govAssistantSub: "ମହାରାଷ୍ଟ୍ର ସରକାର ଡିଜିଟାଲ୍ ଯୋଗାଯୋਗ",
    chatEmptyTitle: "ମୁଁ ଆପଣଙ୍କୁ କିପରି ସାହାଯ୍ୟ କରିପାରିବି?",
  },
};

const DEPARTMENT_LABELS = {
  mr: {
    ALL: "सर्व विभाग",
    GAD: "सामान्य प्रशासन",
    FIN: "वित्त",
    PWD: "सार्वजनिक बांधकाम",
    AGR: "कृषी",
    EDU: "शालेय शिक्षण",
    HED: "उच्च व तंत्र शिक्षण",
    HLT: "आरोग्य",
    HOM: "गृह (पोलीस)",
    REV: "महसूल व वन",
    URB: "नगरविकास",
    RDD: "ग्रामीण विकास",
    WAT: "जलसंपदा",
    ENE: "ऊर्जा",
    IND: "उद्योग",
    LAB: "कामगार",
    SOC: "सामाजिक न्याय",
    TRB: "आदिवासी विकास",
    WCD: "महिला व बाल विकास",
    ENV: "पर्यावरण",
    TOU: "पर्यटन",
    SPT: "क्रीडा व युवक",
    CUL: "सांस्कृतिक कार्य",
    HOU: "गृहनिर्माण",
    TRN: "परिवहन",
    MRT: "बंदर व सागरी",
    FOD: "अन्न व नागरी पुरवठा",
    COP: "सहकार व पणन",
    LAW: "विधी व न्याय",
    PAR: "संसदीय कार्य",
    IT: "माहिती तंत्रज्ञान",
    PR: "जनसंपर्क",
    SKL: "कौशल्य विकास",
    DIS: "आपत्ती व्यवस्थापन",
    ANH: "पशुसंवर्धन व दुग्धव्यवसाय",
    FSH: "मत्स्यव्यवसाय",
    MIN: "खनिज",
    MED: "वैद्यकीय शिक्षण",
    NRI: "एनआरआय कार्य",
  },
  hi: {
    ALL: "सभी विभाग",
    GAD: "सामान्य प्रशासन",
    FIN: "वित्त",
    PWD: "लोक निर्माण",
    AGR: "कृषि",
    EDU: "स्कूली शिक्षा",
    HED: "उच्च व तकनीकी शिक्षा",
    HLT: "स्वास्थ्य",
    HOM: "गृह (पुलिस)",
    REV: "राजस्व व वन",
    URB: "शहरी विकास",
    RDD: "ग्रामीण विकास",
    WAT: "जल संसाधन",
    ENE: "ऊर्जा",
    IND: "उद्योग",
    LAB: "श्रम",
    SOC: "सामाजिक न्याय",
    TRB: "जनजातीय विकास",
    WCD: "महिला व बाल विकास",
    ENV: "पर्यावरण",
    TOU: "पर्यटन",
    SPT: "खेल व युवा",
    CUL: "सांस्कृतिक कार्य",
    HOU: "आवास",
    TRN: "परिवहन",
    MRT: "बंदरगाह व समुद्री",
    FOD: "खाद्य व नागरिक आपूर्ति",
    COP: "सहकार व विपणन",
    LAW: "विधि व न्यायपालिका",
    PAR: "संसदीय कार्य",
    IT: "सूचना प्रौद्योगिकी",
    PR: "जनसंपर्क",
    SKL: "कौशल विकास",
    DIS: "आपदा प्रबंधन",
    ANH: "पशुपालन व डेयरी",
    FSH: "मत्स्य",
    MIN: "खनन",
    MED: "चिकित्सा शिक्षा",
    NRI: "एनआरआई कार्य",
  },
};

const STATE_LABELS = {
  mr: { MH: "महाराष्ट्र" },
  hi: { MH: "महाराष्ट्र" },
};

const getUiText = (language, key, vars = {}) => {
  const template = (UI_TEXT[language] || UI_TEXT.en)[key] || UI_TEXT.en[key] || key;
  return template.replace(/\{(\w+)\}/g, (_, name) => vars[name] ?? "");
};

const getDepartmentLabel = (dept, language) => {
  if (!dept) return "";
  return DEPARTMENT_LABELS[language]?.[dept.code] || dept.label;
};

const getStateLabel = (state, language) => {
  if (!state) return "";
  return STATE_LABELS[language]?.[state.code] || state.name;
};

const PLACEHOLDER_BY_LANG = {
  mr: "मी तुम्हाला कशी मदत करू?",
  hi: "मैं आपकी क्या मदद करूँ?",
  en: "How can I help you?",
  bn: "কীভাবে সাহায্য করতে পারি?",
  te: "నేను ఎలా సహాయపడగలను?",
  ta: "நான் எவ்வாறு உதவட்டும்?",
  gu: "હું શું મદદ કરી શકું?",
  kn: "ನಾನು ಹೇಗೆ ಸಹಾಯ ಮಾಡಲಿ?",
  ml: "ഞാൻ എങ്ങനെ സഹായിക്കേണ്ടത്?",
  pa: "ਮੈਂ ਕੀ ਮਦਦ ਕਰ ਸਕਦਾ ਹਾਂ?",
  ur: "میں کیا مدد کر سکتا ہوں؟",
  or: "ମୁଁ କିପରି ସାହାଯ୍ୟ କରିବି?",
};

const SUGGESTIONS_BY_LANG = {
  mr: ["GR म्हणजे काय?", "निविदा कशी भरावी?", "शेतकरी योजना", "शिष्यवृत्ती माहिती"],
  hi: ["GR क्या होता है?", "टेंडर कैसे भरें?", "किसान योजनाएं", "छात्रवृत्ति जानकारी"],
  en: ["What is a GR?", "How to fill a tender?", "Farmer schemes", "Scholarship info"],
  bn: ["GR কী?", "টেন্ডার কীভাবে পূরণ করবেন?", "কৃষক প্রকল্প", "বৃত্তির তথ্য"],
  te: ["GR అంటే ఏమిటి?", "టెండర్ ఎలా నింపాలి?", "రైతు పథకాలు", "స్కాలర్‌షిప్ సమాచారం"],
  ta: ["GR என்றால் என்ன?", "டெண்டர் எவ்வாறு நிரப்புவது?", "விவசாயி திட்டங்கள்", "உதவித்தொகை தகவல்"],
  gu: ["GR એટలે શું?", "ટેન્ડર કેવી રીતે ભરવું?", "ખેડૂત યોજનાઓ", "શિષ્યવૃત્તિ માહિતી"],
  kn: ["GR ಎಂದರೇನು?", "ಟೆಂಡರ್ ಹೇಗೆ ತುಂಬಬೇಕು?", "ರೈತ ಯೋಜನೆಗಳು", "ವೃತ್ತಿ ಮಾಹಿತಿ"],
  ml: ["GR എന്നാൽ എന്ത്?", "ടെൻഡർ എങ്ങനെ പൂരിപ്പിക്കും?", "കർഷക പദ്ധതികൾ", "സ്കോളർഷിപ്പ് വിവരം"],
  pa: ["GR ਕੀ ਹੁੰਦਾ ਹੈ?", "ਟੈਂਡਰ ਕਿਵੇਂ ਭਰੀਏ?", "ਕਿਸਾਨ ਯੋਜਨਾਵਾਂ", "ਵਜ਼ੀਫਾ ਜਾਣਕਾਰੀ"],
  ur: ["GR کیا ہے؟", "ٹینڈر کیسے بھریں؟", "کسان اسکیمیں", "وظیفہ کی معلومات"],
  or: ["GR କ'ଣ?", "ଟେଣ୍ଡର କିପରି ପୂରଣ କରିବେ?", "ଚାଷୀ ଯୋଜନା", "ଛାତ୍ରବୃତ୍ତି ତଥ୍ୟ"],
};

// ─── Generic / small-talk detector ───────────────────────────────────────────
const GENERIC_PATTERNS = [
  /^(hi|hello|hey|हाय|हेलो|नमस्कार|नमस्ते|नमस्‍ते|namaskar|namaste|helo|hii+|helo+|নমস্কার|হ্যালো|నమస్కారం|హలో|வணக்கம்|ஹலோ|નમસ્તે|હેલો|ನಮಸ್ಕಾರ|ಹಲೋ|നമസ്കാരം|ഹലോ|ਸਤਿ ਸ੍ਰੀ ਅਕਾਲ|ਹੈਲੋ|السلام علیکم|سلام|ہیلو|ନମସ୍କାର|ହାଲୋ)[\s!?.]*$/i,
  /^(good\s?(morning|afternoon|evening|night)|सुप्रभात|शुभ\s?संध्या|शुभ\s?रात्री|शुभ\s?दुपार|शुभ\s?दोपहर|সুপ্রভাত|శుభోదయం|காலை வணக்கம்|શુભ સવાર|ಶುಭೋದಯ|സുപ്രഭാതം|ਸੁਵੱਖਤੇ|صبح بخیر|ସୁପ୍ରଭାତ)[\s!?.]*$/i,
  /^(how are you|how r u|कसे आहात|कसे आहेस|काय हाल|कैसे हो|क्या हाल|কেমন আছেন|ఎలా ఉన్నారు|எப்படி இருக்கிறீர்கள்|કેમ છો|ಹೇಗಿದ್ದೀರಿ|സുഖമാണോ|ਕਿਵੇਂ ਹੋ|کیسے ہیں|କେମିତି ଅଛନ୍ତି)[\s!?.]*$/i,
  /^(thanks|thank you|धन्यवाद|आभारी आहे|आभारी|शुक्रिया|ধন্যবাদ|ధన్యవాదాలు|நன்றி|આભાર|ಧನ್ಯವಾದಗಳು|നന്ദി|ਸ਼ੁਕਰੀਆ|شکریہ|ଧନ୍ୟବାଦ|thx|ty)[\s!?.]*$/i,
  /^(ok|okay|ठीक आहे|ठीक|ठीक है|accha|achha|अच्छा|ঠিক আছে|సరే|சரி|બરાબર|ಸರಿ|ശരി|ਠੀਕ ਹੈ|ٹھیک ہے|ଠିକ ଅଛି)[\s!?.]*$/i,
  /^(bye|goodbye|निरोप|भेटू|अलविदा|tata|cya|বিদায়|వీడ్కోలు|பிரியாவிடை|આવજો|ವಿದಾಯ|വിട|ਅਲਵਿਦਾ|الوداع|ବିଦାୟ)[\s!?.]*$/i,
];

const GENERIC_REPLIES_BY_LANG = {
  en: [
    "Hello! 👋 I am the GR/Tender AI Assistant. Feel free to ask me about Government Resolutions (GRs), tenders, or government schemes — I am ready to help!",
    "Hi! 😊 I can provide information on Government Resolutions (GRs), tenders, and government schemes. Please ask your question.",
    "Greetings! 🙏 How can I assist you with government orders, tenders, or schemes today?",
  ],
  mr: [
    "नमस्कार! 🙏 मी GR/Tender AI Assistant आहे. तुम्ही शासन निर्णय (GR), निविदा, योजना किंवा सरकारी माहितीबद्दल प्रश्न विचारू शकता.",
    "नमस्कार! 😊 मी तुम्हाला महाराष्ट्र शासनाच्या GR, टेंडर आणि सरकारी योजनांबद्दल माहिती देऊ शकतो. कृपया तुमचा प्रश्न विचारा.",
    "हाय! 👋 मला शासन निर्णय, निविदा किंवा सरकारी योजनांबद्दल विचारा — मी मदत करण्यास तत्पर आहे!",
  ],
  hi: [
    "नमस्ते! 🙏 मैं GR/Tender AI Assistant हूँ। आप मुझसे शासन निर्णय (GR), निविदा (टेंडर), योजनाओं या सरकारी जानकारी के बारे में पूछ सकते हैं।",
    "नमस्ते! 😊 मैं आपको सरकारी संकल्पों (GR), टेंडर और सरकारी योजनाओं के बारे में जानकारी दे सकता हूँ। कृपया अपना प्रश्न पूछें।",
    "हेलो! 👋 मुझसे शासन निर्णय, निविदा या सरकारी योजनाओं के बारे में पूछें — मैं मदद करने के लिए तैयार हूँ!",
  ],
  bn: [
    "নমস্কার! 🙏 আমি GR/Tender AI Assistant। আপনি সরকারি সিদ্ধান্ত (GR), টেন্ডার বা সরকারি প্রকল্প সম্পর্কে প্রশ্ন করতে পারেন।",
    "হ্যালো! 👋 আমাকে সরকারি আদেশ, টেন্ডার বা প্রকল্প সম্পর্কে জিজ্ঞাসা করুন — আমি সাহায্য করতে প্রস্তুত!",
  ],
  te: [
    "నమస్కారం! 🙏 నేను GR/Tender AI Assistant ని. మీరు ప్రభుత్వ నిర్ణయాలు (GR), టెండర్లు లేదా ప్రభుత్వ పథకాల గురించి అడగవచ్చు.",
    "హలో! 👋 ప్రభుత్వ ఆదేశాలు, టెండర్లు లేదా సంక్షేమ పథకాల గురించి నన్ను అడగండి — నేను సహాయం చేయడానికి సిద్ధంగా ఉన్నాను!",
  ],
  ta: [
    "வணக்கம்! 🙏 நான் GR/Tender AI Assistant. அரசாணை (GR), டெண்டர்கள் அல்லது அரசு திட்டங்கள் குறித்து நீங்கள் கேட்கலாம்.",
    "ஹலோ! 👋 அரசாணைகள், டெண்டர்கள் அல்லது திட்டங்கள் பற்றி என்னிடம் கேளுங்கள் — உதவ நான் தயார்!",
  ],
  gu: [
    "નમસ્તે! 🙏 હું GR/Tender AI Assistant છું. તમે સરકારી નિર્ણયો (GR), ટેન્ડર અથવા યોજનાઓ વિશે પૂછી શકો છો.",
    "હેલો! 👋 મને સરકારી નિર્ણયો, ટેન્ડર અથવા યોજનાઓ વિશે પૂછો — હું મદદ કરવા તૈયાર છું!",
  ],
  kn: [
    "ನಮಸ್ಕಾರ! 🙏 ನಾನು GR/Tender AI Assistant. ನೀವು ಸರ್ಕಾರಿ ಆದೇಶಗಳು (GR), ಟೆಂಡರ್‌ಗಳು ಅಥವಾ ಸರ್ಕಾರಿ ಯೋಜನೆಗಳ ಬಗ್ಗೆ ಕೇಳಬಹುದು.",
    "ಹಲೋ! 👋 ಸರ್ಕಾರಿ ಆದೇಶಗಳು, ಟೆಂಡರ್‌ಗಳು ಅಥವಾ ಯೋಜನೆಗಳ ಬಗ್ಗೆ ನನ್ನನ್ನು ಕೇಳಿ — ನಾನು ಸಹಾಯ ಮಾಡಲು ಸಿದ್ಧನಿದ್ದೇನೆ!",
  ],
  ml: [
    "നമസ്കാരം! 🙏 ഞാൻ GR/Tender AI Assistant ആണ്. സർക്കാർ ഉത്തരവുകൾ (GR), ടെൻഡറുകൾ അല്ലെങ്കിൽ സർക്കാർ പദ്ധതികൾ എന്നിവയെക്കുറിച്ച് ചോദിക്കാം.",
    "ഹലോ! 👋 സർക്കാർ ഉത്തരവുകൾ, ടെൻഡറുകൾ അല്ലെങ്കിൽ പദ്ധതികൾ എന്നിവയെക്കുറിച്ച് ചോദിക്കൂ — സഹായിക്കാൻ ഞാൻ തയ്യാറാണ്!",
  ],
  pa: [
    "ਸਤਿ ਸ੍ਰੀ ਅਕਾਲ! 🙏 ਮੈਂ GR/Tender AI Assistant ਹਾਂ। ਤੁਸੀਂ ਸਰਕਾਰੀ ਫੈਸਲਿਆਂ (GR), ਟੈਂਡਰਾਂ ਜਾਂ ਸਰਕਾਰੀ ਸਕੀਮਾਂ ਬਾਰੇ ਪੁੱਛ ਸਕਦੇ ਹੋ।",
    "ਹੈਲੋ! 👋 ਮੈਨੂੰ ਸਰਕਾਰੀ ਹੁਕਮਾਂ, ਟੈਂਡਰਾਂ ਜਾਂ ਸਕੀਮਾਂ ਬਾਰੇ ਪੁੱਛੋ — ਮੈਂ ਮਦਦ ਲਈ ਤਿਆਰ ਹਾਂ!",
  ],
  ur: [
    "السلام علیکم! 🙏 میں GR/Tender AI Assistant ہوں۔ آپ حکومتی قراردادوں (GR)، ٹینڈرز یا اسکیموں کے بارے में سوال پوچھ سکتے ہیں۔",
    "ہیلو! 👋 مجھ سے سرکاری احکامات، ٹینڈرز یا اسکیموں کے بارے میں پوچھیں — میں مدد کے لیے تیار ہوں!",
  ],
  or: [
    "ନମସ୍କାର! 🙏 ମୁଁ GR/Tender AI Assistant। ଆପଣ ସରକାରୀ ନିଷ୍ପତ୍ତି (GR), ଟେଣ୍ଡର କିମ୍ବା ଯୋଜନା ବିଷୟରେ ପଚାରିପାରିବେ।",
    "ହାଲୋ! 👋 ମୋତେ ସରକାରୀ ନିର୍ଦ୍ଦେଶ, ଟେଣ୍ଡର କିମ୍ବା ଯୋଜନା ବିଷୟରେ ପଚାରନ୍ତୁ — ମୁଁ ସାହାଯ୍ୟ କରିବାକୁ ପ୍ରସ୍ତୁତ!",
  ],
};

const THANKS_REPLIES_BY_LANG = {
  en: [
    "You're welcome! 🙏 Feel free to ask if you have any more questions.",
    "Glad to help! 😊 Let me know if you need any other information.",
  ],
  mr: [
    "धन्यवाद! 🙏 आणखी काही प्रश्न असल्यास नक्की विचारा.",
    "आनंद झाला मदत करताना! 😊 इतर काही जाणून घ्यायचे असल्यास विचारा.",
  ],
  hi: [
    "आपका स्वागत है! 🙏 यदि कोई और प्रश्न हो तो अवश्य पूछें।",
    "मदद करके खुशी हुई! 😊 कुछ और जानना हो तो पूछ सकते हैं।",
  ],
  bn: [
    "আপনাকে স্বাগতম! 🙏 আরও প্রশ্ন থাকলে নির্দ্বিধায় জিজ্ঞাসা করুন।",
  ],
  te: [
    "స్వాగతం! 🙏 ఇంకా ఏవైనా ప్రశ్నలు ఉంటే తప్పకుండా అడగండి.",
  ],
  ta: [
    "நன்றி! 🙏 மேலும் ஏதேனும் கேள்விகள் இருந்தால் தயங்காமல் கேளுங்கள்.",
  ],
  gu: [
    "આભાર! 🙏 હજુ કોઈ પ્રશ્ન હોય તો જરૂર પૂછો.",
  ],
  kn: [
    "ಧನ್ಯವಾದಗಳು! 🙏 ಇನ್ನಷ್ಟು ಪ್ರಶ್ನೆಗಳಿದ್ದರೆ ಖಂಡಿತ ಕೇಳಿ.",
  ],
  ml: [
    "സ്വാഗതം! 🙏 കൂടുതൽ സംശയങ്ങളുണ്ടെങ്കിൽ ചോദിക്കാം.",
  ],
  pa: [
    "ਧੰਨਵਾਦ! 🙏 ਜੇ ਕੋਈ ਹੋਰ ਸਵਾਲ ਹੋਵੇ ਤਾਂ ਜ਼ਰੂਰ ਪੁੱਛੋ।",
  ],
  ur: [
    "خوش آمدید! 🙏 اگر مزید کوئی سوال ہو تو بلا جھجھک پوچھیں۔",
  ],
  or: [
    "ଧନ୍ୟବାଦ! 🙏 ଆଉ କିଛି ପ୍ରଶ୍ନ ଥିଲେ ନିଶ୍ଚୟ ପଚାରନ୍ତୁ।",
  ],
};

const isGenericMessage = (text) => GENERIC_PATTERNS.some((p) => p.test(text.trim()));
const isThanks = (text) =>
  /^(thanks|thank you|धन्यवाद|आभारी|शुक्रिया|ধন্যবাদ|ధన్యవాదాలు|நன்றி|આભાર|ಧನ್ಯವಾದಗಳು|നന്ദി|ਸ਼ੁਕਰੀਆ|شکریہ|ଧନ୍ୟବାଦ|thx|ty)[\s!?.]*$/i.test(text.trim());
const randomPick = (arr) => arr[Math.floor(Math.random() * arr.length)];

/**
 * Resolves the language for generic responses:
 * Prioritizes the user's message language (if clear keywords/script match),
 * otherwise defaults to the currently selected UI language.
 */
const resolveMessageLanguage = (text, currentUiLang = "en") => {
  const trimmed = (text || "").trim();

  // 1. Script checks for specific Indic languages
  if (/[\u0980-\u09FF]/.test(trimmed)) return "bn"; // Bengali
  if (/[\u0C00-\u0C7F]/.test(trimmed)) return "te"; // Telugu
  if (/[\u0B80-\u0BFF]/.test(trimmed)) return "ta"; // Tamil
  if (/[\u0A80-\u0AFF]/.test(trimmed)) return "gu"; // Gujarati
  if (/[\u0C80-\u0CFF]/.test(trimmed)) return "kn"; // Kannada
  if (/[\u0D00-\u0D7F]/.test(trimmed)) return "ml"; // Malayalam
  if (/[\u0A00-\u0A7F]/.test(trimmed)) return "pa"; // Punjabi
  if (/[\u0600-\u06FF]/.test(trimmed)) return "ur"; // Urdu
  if (/[\u0B00-\u0B7F]/.test(trimmed)) return "or"; // Odia

  // 2. Devanagari script (Marathi vs Hindi)
  if (/[\u0900-\u097F]/.test(trimmed)) {
    if (/(कसे आहात|कसे आहेस|काय हाल|आभारी|भेटू|निरोप|नमस्कार|शुभ दुपार|शासन निर्णय)/i.test(trimmed)) {
      return "mr";
    }
    if (/(नमस्ते|कैसे हो|क्या हाल|शुक्रिया|अलविदा|सुप्रभात|शुभ दोपहर)/i.test(trimmed)) {
      return "hi";
    }
    return currentUiLang === "hi" ? "hi" : "mr";
  }

  // 3. English / Latin script
  if (/^[a-zA-Z0-9\s!?.,'\-—()]+$/.test(trimmed)) {
    if (currentUiLang === "en" || /^(hi|hello|hey|good\s?(morning|afternoon|evening|night)|how are you|how r u|thanks|thank you|thx|ty|ok|okay|bye|goodbye|tata)[\s!?.]*$/i.test(trimmed)) {
      return "en";
    }
  }

  // 4. Default to current UI language or English
  return currentUiLang || "en";
};

const isPlausibleGrReference = (text) => {
  const trimmed = text.trim();
  // 1. Standard 15-20 digit Sanketik / reference ID (ASCII or Devanagari numerals)
  if (/^[\d\u0966-\u096F]{15,20}$/.test(trimmed)) return true;
  // 2. Scientific notation representation from Excel exports (e.g., 2.02401E+17)
  if (/^\d\.\d+[eE]\+?\d+$/i.test(trimmed)) return true;
  // 3. Known GR reference / test ID formats (e.g., FAKE-GR-1234, GR-2024..., CR-...)
  if (/^(?:fake[-_]?gr[-_0-9a-z]*|gr[-_\s#:]*[\d\w/-]{4,}|cr[-_\s#:]*[\d\w/-]{4,})$/i.test(trimmed)) return true;
  return false;
};

const isGarbageText = (text) => {
  const trimmed = text.trim();
  if (trimmed.length < 4) return false;
  // Allow plausible GR reference numbers through to the backend (P1 Bug #1 fix)
  if (isPlausibleGrReference(trimmed)) return false;
  const letterMatches = trimmed.match(/[\u0900-\u097Fa-zA-Z\u0980-\u0DFF]/g) || [];
  const letterRatio = letterMatches.length / trimmed.length;
  if (letterRatio < 0.25 && trimmed.length > 7) return true;
  if (/[^\u0900-\u097Fa-zA-Z0-9\s.,?!।'"()—\u0980-\u0DFF-]{5,}/.test(trimmed)) return true;
  return false;
};

const GARBAGE_REPLY_BY_LANG = {
  en: "Sorry, I couldn't understand your question. Please ask a clear question related to Government Resolutions (GRs), tenders, or government schemes.",
  mr: "क्षमस्व, तुमचा प्रश्न समजला नाही. कृपया शासन निर्णय (GR), निविदा किंवा सरकारी माहितीसंबंधी स्पष्ट प्रश्न विचारा.",
  hi: "क्षमा करें, आपका प्रश्न समझ नहीं आया। कृपया शासन निर्णय (GR), टेंडर या सरकारी योजनाओं से संबंधित स्पष्ट प्रश्न पूछें।",
  bn: "দুঃখিত, আপনার প্রশ্নটি বোঝা যায়নি। দয়া করে সরকারি সিদ্ধান্ত (GR), টেন্ডার বা প্রকল্প সংক্রান্ত স্পষ্ট প্রশ্ন জিজ্ঞাসা করুন।",
  te: "క్షమించండి, మీ ప్రశ్న అర్థం కాలేదు. దయచేసి ప్రభుత్వ నిర్ణయాలు (GR), టెండర్లు లేదా పథకాలకు సంబంధించి స్పష్టమైన ప్రశ్న అడగండి.",
  ta: "மன்னிக்கவும், உங்கள் கேள்வி புரியவில்லை. தயவுசெய்து அரசாணை (GR), டெண்டர்கள் அல்லது திட்டங்கள் குறித்த தெளிவான கேள்வியைக் கேளுங்கள்.",
  gu: "માફ કરશો, તમારો પ્રશ્ન સમજાયો નહીં. કૃપા કરીને સરકારી નિર્ણયો (GR), ટેન્ડર અથવા યોજનાઓ સંબંધિત સ્પષ્ટ પ્રશ્ન પૂછો.",
  kn: "ಕ್ಷಮಿಸಿ, ನಿಮ್ಮ ಪ್ರಶ್ನೆ ಅರ್ಥವಾಗಲಿಲ್ಲ. ದಯವಿಟ್ಟು ಸರ್ಕಾರಿ ಆದೇಶಗಳು (GR), ಟೆಂಡರ್‌ಗಳು ಅಥವಾ ಯೋಜನೆಗಳಿಗೆ ಸಂಬಂಧಿಸಿದ ಸ್ಪಷ್ಟ ಪ್ರಶ್ನೆಯನ್ನು ಕೇಳಿ.",
  ml: "ക്ഷമിക്കണം, ചോദ്യം മനസ്സിലായില്ല. സർക്കാർ ഉത്തരവുകൾ (GR), ടെൻഡറുകൾ അല്ലെങ്കിൽ പദ്ധതികൾ സംബന്ധിച്ച് വ്യക്തമായ ചോദ്യം ചോദിക്കൂ.",
  pa: "ਮਾਫ਼ ਕਰਨਾ, ਤੁਹਾਡਾ ਸਵਾਲ ਸਮਝ ਨਹੀਂ ਆਇਆ। ਕਿਰਪਾ ਕਰਕੇ ਸਰਕਾਰੀ ਹੁਕਮਾਂ (GR), ਟੈਂਡਰਾਂ ਜਾਂ ਸਕੀਮਾਂ ਸੰਬੰਧੀ ਸਪਸ਼ਟ ਸਵਾਲ ਪੁੱਛੋ।",
  ur: "معذرت، آپ کا سوال سمجھ نہیں آیا۔ برائے مہربانی حکومتی فیصلوں (GR)، ٹینڈرز یا اسکیموں کے متعلق واضح سوال پوچھیں۔",
  or: "କ୍ଷମା କରିବେ, ଆପଣଙ୍କ ପ୍ରଶ୍ନ ବୁଝାପଡ଼ିଲା ନାହିଁ। ଦୟାକରି ସରକାରୀ ନିଷ୍ପତ୍ତି (GR), ଟେଣ୍ଡର କିମ୍ବା ଯୋଜନା ସମ୍ବନ୍ଧୀୟ ସ୍ପଷ୍ଟ ପ୍ରଶ୍ନ ପଚାରନ୍ତୁ।",
};

const THINKING_STAGES = {
  mr: [
    { icon: "", label: "विचार करत आहे", minMs: 0 },
    { icon: "", label: "माहिती शोधत आहे", minMs: 2000 },
    { icon: "", label: "PDF तपासत आहे", minMs: 4500 },
    { icon: "", label: "सारांश तयार करत आहे", minMs: 7000 },
    { icon: "", label: "उत्तर तयार होत आहे", minMs: 10000 },
  ],
  hi: [
    { icon: "", label: "सोच रहा है", minMs: 0 },
    { icon: "", label: "जानकारी खोज रहा है", minMs: 2000 },
    { icon: "", label: "PDF की जांच कर रहा है", minMs: 4500 },
    { icon: "", label: "सारांश तैयार कर रहा है", minMs: 7000 },
    { icon: "", label: "उत्तर तैयार हो रहा है", minMs: 10000 },
  ],
  en: [
    { icon: "", label: "Thinking", minMs: 0 },
    { icon: "", label: "Searching for information", minMs: 2000 },
    { icon: "", label: "Checking PDF", minMs: 4500 },
    { icon: "", label: "Preparing summary", minMs: 7000 },
    { icon: "", label: "Generating answer", minMs: 10000 },
  ]
};

const GENERIC_STAGES = {
  mr: [{ icon: "", label: "उत्तर लिहिले जात आहे", minMs: 0 }],
  hi: [{ icon: "", label: "उत्तर लिखा जा रहा है", minMs: 0 }],
  en: [{ icon: "", label: "Writing answer", minMs: 0 }],
  bn: [{ icon: "", label: "উত্তর লেখা হচ্ছে", minMs: 0 }],
  te: [{ icon: "", label: "సమాధానం రాయబడుతోంది", minMs: 0 }],
  ta: [{ icon: "", label: "பதில் எழுதப்படுகிறது", minMs: 0 }],
  gu: [{ icon: "", label: "જવાબ લખાઈ રહ્યો છે", minMs: 0 }],
  kn: [{ icon: "", label: "ಉತ್ತರ ಬರೆಯಲಾಗುತ್ತಿದೆ", minMs: 0 }],
  ml: [{ icon: "", label: "മറുപടി എഴുതുന്നു", minMs: 0 }],
  pa: [{ icon: "", label: "ਜਵਾਬ ਲਿਖਿਆ ਜਾ ਰਿਹਾ ਹੈ", minMs: 0 }],
  ur: [{ icon: "", label: "جواب لکھا جا رہا ہے", minMs: 0 }],
  or: [{ icon: "", label: "ଉତ୍ତର ଲେଖାଯାଉଛି", minMs: 0 }],
};

// ─── AiThinkingSpinner ────────────────────────────────────────────────────────
function AiThinkingSpinner() {
  return (
    <div className="assistant-avatar assistant-avatar--lottie assistant-avatar--spinner" aria-hidden="true">
      <svg className="ai-spinner-svg" viewBox="0 0 32 32" width="22" height="22">
        <circle className="spinner-dot dot-1" cx="20.5" cy="8.21" r="2.3" />
        <circle className="spinner-dot dot-2" cx="25" cy="16" r="2.3" />
        <circle className="spinner-dot dot-3" cx="20.5" cy="23.79" r="2.3" />
        <circle className="spinner-dot dot-4" cx="11.5" cy="23.79" r="2.3" />
        <circle className="spinner-dot dot-5" cx="7" cy="16" r="2.3" />
        <circle className="spinner-dot dot-6" cx="11.5" cy="8.21" r="2.3" />
      </svg>
    </div>
  );
}

// ─── ThinkingIndicator ────────────────────────────────────────────────────────
function ThinkingIndicator({ isGeneric = false, language = "mr" }) {
  const langKey = (isGeneric ? GENERIC_STAGES[language] : THINKING_STAGES[language]) ? language : "en";
  const stages = isGeneric ? (GENERIC_STAGES[langKey] || GENERIC_STAGES.en) : (THINKING_STAGES[langKey] || THINKING_STAGES.en);
  const [stageIdx, setStageIdx] = useState(0);
  const [dots, setDots] = useState("");

  useEffect(() => {
    if (isGeneric) return;
    const timers = stages.slice(1).map((s, i) => setTimeout(() => setStageIdx(i + 1), s.minMs));
    return () => timers.forEach(clearTimeout);
  }, [isGeneric]);

  useEffect(() => {
    const id = setInterval(() => setDots((d) => (d.length >= 3 ? "" : d + ".")), 400);
    return () => clearInterval(id);
  }, []);

  const { icon, label } = stages[Math.min(stageIdx, stages.length - 1)];
  return (
    <div className="thinking-indicator">
      {icon && <span className="thinking-icon">{icon}</span>}
      <span className="thinking-label">{label}<span className="thinking-dots">{dots}</span></span>
    </div>
  );
}

// ─── Markdown renderer ────────────────────────────────────────────────────────
function MarkdownRenderer({ text }) {
  if (!text) return null;
  const lines = text.split("\n");
  const elements = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (/^####\s+/.test(line)) { elements.push(<h4 key={i} className="md-h4">{renderInline(line.replace(/^####\s+/, ""))}</h4>); i++; continue; }
    if (/^###\s+/.test(line)) { elements.push(<h3 key={i} className="md-h3">{renderInline(line.replace(/^###\s+/, ""))}</h3>); i++; continue; }
    if (/^##\s+/.test(line)) { elements.push(<h2 key={i} className="md-h2">{renderInline(line.replace(/^##\s+/, ""))}</h2>); i++; continue; }
    if (/^(\s*[-*•]|\s{2,}[◦▪])\s+/.test(line)) {
      const indent = line.match(/^(\s*)/)[1].length;
      const content = line.replace(/^\s*[-*•◦▪]\s+/, "");
      elements.push(<li key={i} className={`md-li ${indent > 0 ? "md-li-sub" : ""}`}>{renderInline(content)}</li>);
      i++; continue;
    }
    const numMatch = line.match(/^(\s*)(\d+)\.\s+(.*)$/);
    if (numMatch) {
      const indent  = numMatch[1].length;
      const num     = numMatch[2];
      const content = numMatch[3];
      elements.push(
        <li key={i} className={`md-li md-li-ordered ${indent > 0 ? "md-li-sub" : ""}`}>
          <span className="md-ol-num">{num}.</span>
          <span className="md-ol-content">{renderInline(content)}</span>
        </li>
      );
      i++; continue;
    }
    if (line.trim() === "") { elements.push(<div key={i} className="md-spacer" />); i++; continue; }
    elements.push(<p key={i} className="md-p">{renderInline(line)}</p>);
    i++;
  }
  return <div className="markdown-body">{elements}</div>;
}

function renderInline(text) {
  if (!text) return null;
  const tokenRegex = /(\[[^\]]+\]\([^\)]+\)|\*\*.*?\*\*)/g;
  const parts = text.split(tokenRegex);
  return parts.map((part, i) => {
    if (!part) return null;
    if (/^\*\*.*\*\*$/.test(part)) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    const linkMatch = part.match(/^\[([^\]]+)\]\((https?:\/\/[^\s\)]+)\)$/);
    if (linkMatch) {
      return (
        <a
          key={i}
          href={linkMatch[2]}
          target="_blank"
          rel="noopener noreferrer"
          className="md-link"
        >
          {linkMatch[1]}
        </a>
      );
    }
    return <span key={i}>{part}</span>;
  });
}

function normalizeMessages(messages = []) {
  return messages.map((msg) => {
    const sources = msg.metadata?.sources;
    return { ...msg, sources: Array.isArray(sources) && sources.length > 0 ? sources : undefined };
  });
}

// ─── Message Actions Toolbar (Copy / Version Pager / Feedback / Retry) ────────
function MessageActions({
  msg,
  msgIndex,
  onRetry,
  onEdit,
  onVersionChange,
  onShare,
  isRegenerating = false,
}) {
  const [copied, setCopied] = useState(false);
  const [feedback, setFeedback] = useState(null); // 'like' | 'dislike' | null
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const moreMenuRef = useRef(null);

  // Close more menu on click outside
  useEffect(() => {
    const handleOutside = (e) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target)) {
        setMoreMenuOpen(false);
      }
    };
    if (moreMenuOpen) {
      document.addEventListener("mousedown", handleOutside);
      return () => document.removeEventListener("mousedown", handleOutside);
    }
  }, [moreMenuOpen]);

  const copyTextToClipboard = async (text) => {
    if (!text) return false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (_) { }

    try {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const success = document.execCommand("copy");
      textArea.remove();
      return success;
    } catch (err) {
      console.error("Clipboard copy error:", err);
      return false;
    }
  };

  const handleCopy = async () => {
    if (!msg?.content) return;
    const ok = await copyTextToClipboard(msg.content);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
    setMoreMenuOpen(false);
  };

  const handleShare = () => {
    if (!msg?.content) return;
    if (onShare) {
      onShare(msg, msgIndex);
    }
    setMoreMenuOpen(false);
  };

  const handleCopyPlainText = async () => {
    if (!msg?.content) return;
    const plainText = msg.content
      .replace(/#{1,6}\s+/g, "")
      .replace(/\*\*(.*?)\*\*/g, "$1")
      .replace(/\*(.*?)\*/g, "$1")
      .replace(/\[(.*?)\]\(.*?\)/g, "$1")
      .replace(/`{1,3}(.*?)`{1,3}/g, "$1")
      .replace(/>\s+/g, "");

    const ok = await copyTextToClipboard(plainText);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
    setMoreMenuOpen(false);
  };

  const handleDownload = () => {
    if (!msg?.content) return;
    try {
      const blob = new Blob([msg.content], { type: "text/plain;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `grgpt-response-${Date.now()}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export error:", err);
    }
    setMoreMenuOpen(false);
  };

  const versions = Array.isArray(msg?.versions) && msg.versions.length > 0
    ? msg.versions
    : (Array.isArray(msg?.metadata?.versions) && msg.metadata.versions.length > 0 ? msg.metadata.versions : null);
  const hasMultipleVersions = versions && versions.length > 1;
  const currentIdx = typeof msg?.versionIndex === "number"
    ? msg.versionIndex
    : (typeof msg?.metadata?.versionIndex === "number" ? msg.metadata.versionIndex : (versions ? versions.length - 1 : 0));
  const totalVersions = versions ? versions.length : 1;
  const currentVersion = currentIdx + 1;

  return (
    <div className={`message-actions-toolbar ${msg.role} ${copied ? "is-copied" : ""}`}>
      {/* ── Version Pagination: ⟨ 2 / 2 ⟩ ── */}
      {msg.role === "assistant" && hasMultipleVersions && (
        <div className="msg-version-paginator">
          <button
            type="button"
            className="msg-version-nav-btn"
            disabled={currentIdx <= 0}
            onClick={() => onVersionChange && onVersionChange(msgIndex, currentIdx - 1)}
            aria-label="Previous version"
            title="Previous response"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>

          <span className="msg-version-text">
            {currentVersion} / {totalVersions}
          </span>

          <button
            type="button"
            className="msg-version-nav-btn"
            disabled={currentIdx >= totalVersions - 1}
            onClick={() => onVersionChange && onVersionChange(msgIndex, currentIdx + 1)}
            aria-label="Next version"
            title="Next response"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>
      )}

      {/* Copy Button */}
      <button
        type="button"
        className={`msg-action-icon-btn ${copied ? "copied" : ""}`}
        onClick={handleCopy}
        title={copied ? "Copied!" : "Copy response"}
        aria-label="Copy response"
      >
        {copied ? (
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        ) : (
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
          </svg>
        )}
      </button>

      {/* Share / Export Button */}
      <button
        type="button"
        className="msg-action-icon-btn"
        onClick={handleShare}
        title="Share public link to response"
        aria-label="Share public link to response"
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
          <polyline points="16 6 12 2 8 6" />
          <line x1="12" y1="2" x2="12" y2="15" />
        </svg>
      </button>

      {/* Thumbs Up (Helpful) */}
      {msg.role === "assistant" && (
        <button
          type="button"
          className={`msg-action-icon-btn ${feedback === "like" ? "active-like" : ""}`}
          onClick={() => setFeedback(feedback === "like" ? null : "like")}
          title={feedback === "like" ? "Liked" : "Helpful response"}
          aria-label="Helpful"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill={feedback === "like" ? "#10B981" : "none"} stroke={feedback === "like" ? "#10B981" : "currentColor"} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
          </svg>
        </button>
      )}

      {/* Thumbs Down (Unhelpful) */}
      {msg.role === "assistant" && (
        <button
          type="button"
          className={`msg-action-icon-btn ${feedback === "dislike" ? "active-dislike" : ""}`}
          onClick={() => setFeedback(feedback === "dislike" ? null : "dislike")}
          title={feedback === "dislike" ? "Disliked" : "Not helpful"}
          aria-label="Not helpful"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill={feedback === "dislike" ? "#EF4444" : "none"} stroke={feedback === "dislike" ? "#EF4444" : "currentColor"} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h3a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2h-3" />
          </svg>
        </button>
      )}

      {/* Edit Button for User Messages */}
      {msg.role === "user" && onEdit && (
        <button
          type="button"
          className="msg-action-icon-btn"
          onClick={onEdit}
          title="Edit prompt"
          aria-label="Edit prompt"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 20h9" />
            <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
          </svg>
        </button>
      )}

      {/* Retry / Regenerate Button for Assistant Messages */}
      {msg.role === "assistant" && onRetry && (
        <button
          type="button"
          className={`msg-action-icon-btn ${isRegenerating ? "spinning" : ""}`}
          onClick={onRetry}
          disabled={isRegenerating}
          title={isRegenerating ? "Regenerating..." : "Regenerate response"}
          aria-label="Regenerate response"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="23 4 23 10 17 10" />
            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
          </svg>
        </button>
      )}

      {/* More Options Button with Working Menu */}
      {msg.role === "assistant" && (
        <div className="msg-more-menu-wrapper" ref={moreMenuRef}>
          <button
            type="button"
            className="msg-action-icon-btn"
            title="More options"
            aria-label="More options"
            onClick={() => setMoreMenuOpen((o) => !o)}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
              <circle cx="5" cy="12" r="2" />
              <circle cx="12" cy="12" r="2" />
              <circle cx="19" cy="12" r="2" />
            </svg>
          </button>

          {moreMenuOpen && (
            <div className="msg-more-menu-dropdown">
              <button
                type="button"
                className="msg-more-menu-item"
                onClick={handleShare}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                  <polyline points="16 6 12 2 8 6" />
                  <line x1="12" y1="2" x2="12" y2="15" />
                </svg>
                Share public link
              </button>

              <button
                type="button"
                className="msg-more-menu-item"
                onClick={handleCopy}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                </svg>
                Copy formatted
              </button>

              <button
                type="button"
                className="msg-more-menu-item"
                onClick={handleCopyPlainText}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <line x1="4" y1="9" x2="20" y2="9" />
                  <line x1="4" y1="15" x2="14" y2="15" />
                  <line x1="10" y1="3" x2="14" y2="21" />
                </svg>
                Copy plain text
              </button>

              <button
                type="button"
                className="msg-more-menu-item"
                onClick={handleDownload}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Download (.txt)
              </button>
            </div>
          )}
        </div>
      )}

    </div>
  );
}

// ─── Language Selector ────────────────────────────────────────────────────────
function LanguageSelector({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = LANGUAGES.find((l) => l.code === value) || LANGUAGES[0];

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="lang-selector" ref={ref}>
      <button className="lang-trigger" onClick={() => setOpen((o) => !o)} title="Choose Language" type="button">
        <span className="lang-globe"><UiIcon name="language" /></span>
        <span className="lang-current">{current.label}</span>
        <span className="lang-current-mobile">{current.code.toUpperCase()}</span>
        <span className={`lang-chevron ${open ? "open" : ""}`}>▾</span>
      </button>
      {open && (
        <div className="lang-dropdown">
          <div className="lang-dropdown-title">Choose Language / भाषा निवडा</div>
          <div className="lang-grid">
            {LANGUAGES.map((lang) => (
              <button key={lang.code} type="button"
                className={`lang-option ${lang.code === value ? "active" : ""}`}
                onClick={() => {
                  localStorage.setItem("grgpt_ui_language", lang.code);
                  window.dispatchEvent(new CustomEvent("grgpt-language-change", { detail: lang.code }));
                  onChange(lang.code);
                  setOpen(false);
                }}>
                <span className="lang-code-badge">{lang.code.toUpperCase()}</span>
                <span className="lang-name">{lang.label}</span>
                {lang.code === value && <span className="lang-check">✓</span>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── State Selector ───────────────────────────────────────────────────────────
function StateSelector({ value, onChange, language, states = [], onOpen }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);
  const searchRef = useRef(null);
  const current = states.find((s) => s.code === value) || states[0] || { code: "MH", name: "Maharashtra", active: true };

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setSearch(""); }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => { if (open) setTimeout(() => searchRef.current?.focus(), 60); }, [open]);

  const filtered = states.filter((s) =>
    getStateLabel(s, language).toLowerCase().includes(search.toLowerCase()) ||
    s.name.toLowerCase().includes(search.toLowerCase()) ||
    s.code.toLowerCase().includes(search.toLowerCase())
  );
  const activeStates = filtered.filter((s) => s.active);
  const comingStates = filtered.filter((s) => !s.active);

  const handleSelect = (state) => { if (!state.active) return; onChange(state.code); setOpen(false); setSearch(""); };

  return (
    <div className="state-selector" ref={ref}>
      <button className="state-trigger" onClick={() => {
        const nextOpen = !open;
        setOpen(nextOpen);
        if (nextOpen && onOpen) onOpen();
      }} title={getUiText(language, "chooseState")} type="button">
        <span className="state-icon"><UiIcon name="state" /></span>
        <span className="state-current">{getStateLabel(current, language)}</span>
        <span className="state-current-mobile">{current.code}</span>
        <span className={`lang-chevron ${open ? "open" : ""}`}>▾</span>
      </button>
      {open && (
        <div className="state-dropdown">
          <div className="state-dropdown-title">{getUiText(language, "selectState")}</div>
          <div className="state-search-wrap">
            <span className="state-search-icon"><UiIcon name="search" /></span>
            <input ref={searchRef} className="state-search-input" type="text" placeholder={getUiText(language, "searchState")}
              value={search} onChange={(e) => setSearch(e.target.value)} />
            {search && <button className="state-search-clear" type="button" onClick={() => setSearch("")}>✕</button>}
          </div>
          <div className="state-list">
            {activeStates.length > 0 && (
              <><div className="state-group-label"><UiIcon name="available" className="state-group-icon state-group-icon--available" />{getUiText(language, "availableNow")}</div>
                {activeStates.map((state) => (
                  <button key={state.code} type="button"
                    className={`state-option state-option--active ${state.code === value ? "state-option--selected" : ""}`}
                    onClick={() => handleSelect(state)}>
                    <span className="state-name">{getStateLabel(state, language)}</span>
                    <span className="state-code-badge">{state.code}</span>
                    {state.code === value && <span className="lang-check">✓</span>}
                  </button>
                ))}
              </>
            )}

            {filtered.length === 0 && <div className="state-no-results">{getUiText(language, "noStateFound", { search })}</div>}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Department Selector (inside input bar) ───────────────────────────────────
function DepartmentSelector({ value, onChange, language }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" ? window.innerWidth <= 480 : false);
  const ref = useRef(null);
  const searchRef = useRef(null);
  const current = GR_DEPARTMENTS.find((d) => d.code === value) || GR_DEPARTMENTS[0];

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 480);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setSearch(""); }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => { if (open) setTimeout(() => searchRef.current?.focus(), 60); }, [open]);

  const filtered = GR_DEPARTMENTS.filter((d) =>
    getDepartmentLabel(d, language).toLowerCase().includes(search.toLowerCase()) ||
    d.label.toLowerCase().includes(search.toLowerCase()) ||
    d.code.toLowerCase().includes(search.toLowerCase())
  );

  const isFiltered = value !== "ALL"; // highlight trigger when a real dept is selected

  return (
    <div className="dept-selector" ref={ref}>
      {/* ── Trigger chip — looks like the "Search ▾" in your screenshot ── */}
      <button
        type="button"
        className={`dept-trigger ${isFiltered ? "dept-trigger--active" : ""}`}
        onClick={() => setOpen((o) => !o)}
        title={getUiText(language, "filterByDepartment")}
      >
        <span className="dept-trigger-icon"><UiIcon name={getDepartmentIconName(current.code)} /></span>
        {!isMobile && (
          <span className="dept-trigger-label">
            {value === "ALL" ? getUiText(language, "department") : getDepartmentLabel(current, language)}
          </span>
        )}
        {!isMobile && <span className={`dept-chevron ${open ? "open" : ""}`}>▾</span>}
      </button>

      {/* ── Dropdown panel — opens upward so it clears the input bar ── */}
      {open && (
        <div className="dept-dropdown">
          <div className="dept-dropdown-header">
            <span className="dept-dropdown-title">{getUiText(language, "filterByDepartment")}</span>
            {isFiltered && (
              <button type="button" className="dept-clear-btn"
                onClick={() => { onChange("ALL"); setOpen(false); setSearch(""); }}>
                {getUiText(language, "clear")} ✕
              </button>
            )}
          </div>

          {/* Search */}
          <div className="dept-search-wrap">
            <span className="dept-search-icon"><UiIcon name="search" /></span>
            <input ref={searchRef} className="dept-search-input" type="text"
              placeholder={getUiText(language, "searchDepartment")}
              value={search} onChange={(e) => setSearch(e.target.value)} />
            {search && (
              <button className="dept-search-clear" type="button" onClick={() => setSearch("")}>✕</button>
            )}
          </div>

          {/* List */}
          <div className="dept-list">
            {filtered.map((dept) => (
              <button key={dept.code} type="button"
                className={`dept-option ${dept.code === value ? "dept-option--selected" : ""}`}
                onClick={() => { onChange(dept.code); setOpen(false); setSearch(""); }}>
                <span className="dept-option-icon"><UiIcon name={getDepartmentIconName(dept.code)} /></span>
                <span className="dept-option-label">{getDepartmentLabel(dept, language)}</span>
                {dept.code === value && <span className="dept-check">✓</span>}
              </button>
            ))}
            {filtered.length === 0 && (
              <div className="dept-no-results">{getUiText(language, "noDepartmentFound", { search })}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// ChatWindow
// ═════════════════════════════════════════════════════════════════════════════
function ChatInputBar({
  centered = false,
  sendMessage,
  language,
  selectedDept,
  setSelectedDept,
  placeholder,
  input,
  setInput,
  handleKeyDown,
  loading,
  genericLoad,
  handleStopGeneration,
  textareaRef,
}) {
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);

  // Tracks the text that was in the input box BEFORE voice started, so
  // interim results can be shown without permanently accumulating into state.
  const committedBaseRef = useRef("");
  const hasFinalRef = useRef(false);

  // Auto-resize textarea to grow taller as the user types a longer prompt
  useEffect(() => {
    const textarea = textareaRef?.current;
    if (!textarea) return;

    const adjustHeight = () => {
      // Temporarily reset height so scrollHeight is calculated accurately when text shrinks
      textarea.style.height = "auto";
      const maxHeight = 200;
      const currentScroll = textarea.scrollHeight;
      if (currentScroll > 0) {
        textarea.style.height = `${Math.min(currentScroll, maxHeight)}px`;
        textarea.style.overflowY = currentScroll > maxHeight ? "auto" : "hidden";
      }
    };

    adjustHeight();

    window.addEventListener("resize", adjustHeight);
    return () => window.removeEventListener("resize", adjustHeight);
  }, [input, textareaRef]);

  const handleToggleVoice = () => {
    if (isListening) {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) { }
      }
      setIsListening(false);
      return;
    }

    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      alert("Voice input is not supported in this browser. Please use Chrome, Edge, or Safari.");
      return;
    }

    try {
      const recognition = new SpeechRecognition();
      recognitionRef.current = recognition;
      recognition.continuous = false;
      recognition.interimResults = true;

      if (language === "mr") recognition.lang = "mr-IN";
      else if (language === "hi") recognition.lang = "hi-IN";
      else recognition.lang = "en-IN";

      recognition.onstart = () => {
        // Snapshot the current input as the committed base before any voice input
        committedBaseRef.current = (input || "").trim();
        hasFinalRef.current = false;
        setIsListening(true);
      };

      recognition.onresult = (event) => {
        let interimText = "";
        let finalText = "";

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const result = event.results[i];
          if (result.isFinal) {
            finalText += result[0].transcript;
          } else {
            interimText += result[0].transcript;
          }
        }

        if (finalText) {
          // A final result arrived — commit it and clear interim display
          hasFinalRef.current = true;
          const base = committedBaseRef.current;
          const committed = base ? `${base} ${finalText.trim()}` : finalText.trim();
          committedBaseRef.current = committed;
          setInput(committed);
        } else if (interimText) {
          // Show interim result without permanently committing it
          const base = committedBaseRef.current;
          const preview = base ? `${base} ${interimText}` : interimText;
          setInput(preview);
        }
      };

      recognition.onerror = (err) => {
        console.warn("Speech recognition error:", err);
        // Restore the committed base on error so interim text is not left in the box
        if (!hasFinalRef.current) {
          setInput(committedBaseRef.current);
        }
        setIsListening(false);
      };

      recognition.onend = () => {
        // If no final result came through, restore the base (discard interim preview)
        if (!hasFinalRef.current) {
          setInput(committedBaseRef.current);
        }
        setIsListening(false);
      };

      recognition.start();
    } catch (e) {
      console.error("Speech recognition start failed:", e);
      setIsListening(false);
    }
  };

  return (
    <div className={`chat-input-wrap ${centered ? "chat-input-wrap--centered" : ""}`}>
      <form className="chat-input-bar" onSubmit={sendMessage}>
        <DepartmentSelector value={selectedDept} onChange={setSelectedDept} language={language} />
        <div className="input-bar-divider" />
        <textarea
          ref={textareaRef}
          placeholder={placeholder}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          maxLength={MAX_CHAT_INPUT_LENGTH}
          rows={1}
        />

        {input.length >= 1000 && (
          <span
            className={`char-counter ${input.length >= MAX_CHAT_INPUT_LENGTH
                ? "limit-reached"
                : input.length >= 1350
                  ? "near-limit"
                  : ""
              }`}
            title={`Character limit: ${input.length}/${MAX_CHAT_INPUT_LENGTH}`}
          >
            {input.length}/{MAX_CHAT_INPUT_LENGTH}
          </span>
        )}

        {/* Microphone Button */}
        <button
          type="button"
          className={`mic-btn ${isListening ? "listening" : ""}`}
          onClick={handleToggleVoice}
          title={isListening ? "Listening... click to stop" : "Voice input"}
          aria-label="Voice input"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
            <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
            <line x1="12" x2="12" y1="19" y2="22" />
          </svg>
        </button>

        {loading || genericLoad ? (
          <button
            type="button"
            onClick={handleStopGeneration}
            className="send-btn stop-btn"
            title="Stop generation"
            aria-label="Stop generation"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
              <rect x="5" y="5" width="14" height="14" rx="2" />
            </svg>
          </button>
        ) : (
          <button type="submit" disabled={loading || genericLoad} className="send-btn" title="Send">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M22 2L11 13" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        )}
      </form>
    </div>
  );
}

export default function ChatWindow() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { activeChatId, chats } = useSelector((s) => s.chat);
  const user = useSelector((state) => state.user.user);
  const credits = useSelector((state) => state.user.credits);
  // System Notice Popup
  const [systemNotice, setSystemNotice] = useState("");
  const [showNotice, setShowNotice] = useState(false);
  const [activeSources, setActiveSources] = useState(null);
  const [regeneratingMsgIndex, setRegeneratingMsgIndex] = useState(null);

  useEffect(() => {
    setActiveSources(null);
    setRegeneratingMsgIndex(null);
  }, [activeChatId]);

  const handleToggleSources = (index, msg) => {
    if (activeSources?.msgIndex === index) {
      setActiveSources(null);
    } else {
      let userQuery = "";
      for (let j = index - 1; j >= 0; j--) {
        if (messages[j]?.role === "user") {
          userQuery = messages[j].content;
          break;
        }
      }
      setActiveSources({
        msgIndex: index,
        sources: msg.sources || [],
        query: userQuery,
      });
    }
  };

  // ── Version Navigation for Regenerated Messages (⟨ 2 / 2 ⟩) ─────────────
  const handleVersionChange = (messageIndex, newVersionIndex) => {
    const chatId = activeChatId;
    if (!chatId) return;
    dispatch(updateMessageVersion({
      chatId,
      messageIndex,
      versionIndex: newVersionIndex,
    }));

    const targetMsg = messages[messageIndex];
    const versions = Array.isArray(targetMsg?.versions) && targetMsg.versions.length > 0
      ? targetMsg.versions
      : (Array.isArray(targetMsg?.metadata?.versions) ? targetMsg.metadata.versions : null);

    if (activeSources?.msgIndex === messageIndex && versions?.[newVersionIndex]) {
      setActiveSources((prev) => prev ? {
        ...prev,
        sources: versions[newVersionIndex].sources || [],
      } : null);
    }
  };

  // ── Regenerate Response (adds new version, preserves old version) ────────
  const handleRegenerateResponse = async (assistantIndex) => {
    if (loading || genericLoad || regeneratingMsgIndex !== null) return;
    const targetAssistantMsg = messages[assistantIndex];
    if (!targetAssistantMsg) return;

    if (!user && guestChatCount >= GUEST_FREE_CHATS) {
      setGuestStep(3);
      return;
    }

    const effectiveCredits =
      credits !== null && credits !== undefined
        ? credits
        : user?.credits !== null && user?.credits !== undefined
        ? user.credits
        : null;

    if (user && effectiveCredits !== null && effectiveCredits <= 0) {
      setCreditMsg(
        language === "mr"
          ? "तुमचे दैनंदिन मोफत क्रेडिट्स संपले आहेत. पुढील संभाषणासाठी कृपया तुमचा प्लॅन अपग्रेड करा."
          : "Your daily credits have run out. Please upgrade your plan to continue chatting without limits."
      );
      setGuestStep(4);
      return;
    }

    let promptText = "";
    for (let j = assistantIndex - 1; j >= 0; j--) {
      if (messages[j]?.role === "user") {
        promptText = messages[j].content;
        break;
      }
    }
    if (!promptText) return;

    let chatId = activeChatId;
    if (!chatId) {
      chatId = "temp-" + Date.now();
      dispatch(setActiveChatId(chatId));
    }

    setRegeneratingMsgIndex(assistantIndex);

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const res = await API.post(
        "/chat/ai",
        {
          message: promptText,
          chatId: chatId.startsWith("temp-") ? null : chatId,
          language,
          state: selectedState,
          department: selectedDepartmentLabel,
          department_code: selectedDept === "ALL" ? "" : selectedDept,
          isRegenerate: true,
          messageIndex: assistantIndex,
        },
        {
          signal: controller.signal,
          timeout: 300000,
        }
      );

      const botReply = res.data.response || "उत्तर उपलब्ध नाही.";
      const sources = res.data.sources || [];
      const updatedCredits = res.data.credits;
      const newChatId = res.data.chatId;

      if (updatedCredits !== null && updatedCredits !== undefined) {
        dispatch(setCredits(updatedCredits));
      }

      if (chatId.startsWith("temp-") && newChatId) {
        const oldTempId = chatId;
        dispatch(migrateChatId({ oldId: oldTempId, newId: newChatId }));
        chatId = newChatId;
        if (activeChatId === oldTempId) dispatch(setActiveChatId(newChatId));
      }

      dispatch(appendMessageVersion({
        chatId,
        messageIndex: assistantIndex,
        newContent: botReply,
        newSources: sources,
      }));

      // Update Sources sidebar if open for this message
      if (activeSources?.msgIndex === assistantIndex) {
        setActiveSources((prev) => prev ? { ...prev, sources } : null);
      }
    } catch (err) {
      console.error("Regenerate response error:", err);
    } finally {
      setRegeneratingMsgIndex(null);
      abortControllerRef.current = null;
    }
  };

  useEffect(() => {
    // Only show once per session
    if (sessionStorage.getItem("system_notice_dismissed")) return;
    getSystemNotice()
      .then(msg => {
        if (msg) {
          setSystemNotice(msg);
          setShowNotice(true);
        }
      })
      .catch(err => {
        console.warn("Could not fetch system notice:", err?.message || err);
      });
  }, []);

  const messages = chats[activeChatId]?.messages || [];
  const loading = chats[activeChatId]?.loading || false;

  const [input, setInput] = useState("");
  const [genericLoad, setGenericLoad] = useState(false);
  const [nextResetAt, setNextResetAt] = useState(null);
  const [lastResetAt, setLastResetAt] = useState(null);
  const abortControllerRef = useRef(null);

  const textareaRef = useRef(null);
  const [editingMsgIndex, setEditingMsgIndex] = useState(null);
  const [editText, setEditText] = useState("");
  const [shareModalData, setShareModalData] = useState(null);

  const handleOpenShare = (msg, msgIndex) => {
    let question = "";
    if (typeof msgIndex === "number" && msgIndex > 0) {
      for (let j = msgIndex - 1; j >= 0; j--) {
        if (messages[j]?.role === "user") {
          question = messages[j].content;
          break;
        }
      }
    }
    const currentChatTitle = chats[activeChatId]?.title || "";
    setShareModalData({
      content: msg.content,
      question: question || (currentChatTitle && currentChatTitle !== "New Chat" ? currentChatTitle : ""),
      title: currentChatTitle || "GRGPT Response",
      sources: msg.sources || msg.metadata?.sources || [],
      metadata: {
        language,
        selectedState,
        selectedDept,
      },
      chatId: activeChatId,
    });
  };

  const handleEditPrompt = (index, text) => {
    setEditingMsgIndex(index);
    setEditText(text);
    setInput(text);
    if (textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const handleStopGeneration = (e) => {
    if (e) e.preventDefault();
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setGenericLoad(false);
    if (activeChatId) {
      dispatch(setLoading({ chatId: activeChatId, value: false }));
      dispatch(addMessage({
        chatId: activeChatId,
        message: {
          role: "assistant",
          content: "🛑 संभाषण थांबवले गेले. (Generation stopped by user.)",
        },
      }));
    }
  };
  const [language, setLanguage] = useState(() => localStorage.getItem("grgpt_ui_language") || "en");
  const transliteratedUserName = useTransliteratedName(user?.name, language);
  const [selectedState, setSelectedState] = useState("MH");
  const [selectedDept, setSelectedDept] = useState("ALL"); // ← new
  const [states, setStates] = useState([]);

  // Sync language with global events
  useEffect(() => {
    const handleLangSync = (e) => {
      if (e.detail) setLanguage(e.detail);
    };
    window.addEventListener("grgpt-language-change", handleLangSync);
    return () => window.removeEventListener("grgpt-language-change", handleLangSync);
  }, []);

  const loadStates = () => {
    API.get("/states")
      .then((res) => {
        if (res.data && Array.isArray(res.data.data)) {
          setStates(res.data.data);
          const hasMH = res.data.data.find(s => s.code === "MH" && s.active);
          if (!hasMH) {
            const firstActive = res.data.data.find(s => s.active);
            if (firstActive) setSelectedState(firstActive.code);
          }
        }
      })
      .catch((err) => {
        console.error("Failed to load states from database:", err);
      });
  };

  useEffect(() => {
    loadStates();
  }, []);

  // ―― Auto-select department from signup preference (one-time) ――――――――――――――――
  useEffect(() => {
    try {
      const raw = localStorage.getItem("grgpt_preferred_dept_names");
      if (!raw) return;
      const names = JSON.parse(raw); // e.g. ["Finance", "Agriculture"]
      if (!Array.isArray(names) || names.length === 0) return;

      // Match first preferred dept name to GR_DEPARTMENTS (case-insensitive, partial)
      const firstPref = names[0].toLowerCase();
      const match = GR_DEPARTMENTS.find((d) =>
        d.label.toLowerCase().includes(firstPref) ||
        firstPref.includes(d.label.toLowerCase())
      );

      if (match) setSelectedDept(match.code);
    } catch (_) { }
    finally {
      // Clear so it only applies once (fresh start)
      localStorage.removeItem("grgpt_preferred_dept_names");
    }
  }, []); // ← runs only on mount

  const welcomeMessage = WELCOME_BY_LANG[language] || WELCOME_BY_LANG.mr;
  const placeholder = PLACEHOLDER_BY_LANG[language] || PLACEHOLDER_BY_LANG.mr;
  const suggestions = SUGGESTIONS_BY_LANG[language] || SUGGESTIONS_BY_LANG.mr;
  const selectedDepartmentMeta = GR_DEPARTMENTS.find((d) => d.code === selectedDept);
  const selectedDepartmentLabel = selectedDept === "ALL" ? "" : (selectedDepartmentMeta?.label || "");

  const [guestStep, setGuestStep] = useState(0);
  const [guestChatCount, setGuestChatCount] = useState(() =>
    parseInt(localStorage.getItem("grgpt_guest_chats") || "0", 10)
  );
  const [creditMsg, setCreditMsg] = useState("");
  const chatEndRef = useRef(null);

  useEffect(() => {
    localStorage.setItem("grgpt_ui_language", language);
    window.dispatchEvent(new CustomEvent("grgpt-language-change", { detail: language }));
  }, [language]);

  useEffect(() => {
    const handleSync = (e) => {
      if (e.detail && e.detail !== language) {
        setLanguage(e.detail);
      }
    };
    window.addEventListener("grgpt-language-change", handleSync);
    return () => window.removeEventListener("grgpt-language-change", handleSync);
  }, [language]);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading, genericLoad]);

  useEffect(() => {
    if (!activeChatId) return;
    if (activeChatId.startsWith("temp-")) return;
    if (chats[activeChatId]?.messages?.length > 0) return;
    loadChat(activeChatId);
  }, [activeChatId]);

  useEffect(() => {
    if (user) {
      localStorage.removeItem("grgpt_guest_chats");
      setGuestChatCount(0);
      return;
    }
    if (guestChatCount >= GUEST_FREE_CHATS) { setGuestStep(3); }
  }, [user, guestChatCount]);

  const loadChat = async (chatId) => {
    try {
      dispatch(setLoading({ chatId, value: true }));
      const res = await API.get(`/chat/${chatId}`);
      dispatch(setMessages({ chatId, messages: normalizeMessages(res.data.messages || []) }));
    } catch (err) { console.error("Load chat failed:", err); }
    finally { dispatch(setLoading({ chatId, value: false })); }
  };

  const handleAuthPopupClose = () => setGuestStep(2);
  const handleWelcomeCardClose = () => setGuestStep(0);
  const handleLimitPopupClose = () => setGuestStep(0);
  const handleCreditPopupClose = () => setGuestStep(0);
  const handlePopupNavigate = (path, opts) => { setGuestStep(0); navigate(path, opts); };

  const handleKeyDown = (e) => {
    if (loading || genericLoad) return;
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const sendMessage = async (e, overrideText, truncateIndex) => {
    if (loading || genericLoad) return;
    if (e) e.preventDefault();

    const userText = (overrideText || input).trim();
    if (!userText) return;

    const isReal = !isGenericMessage(userText) && !isGarbageText(userText);
    if (!user && isReal && guestChatCount >= GUEST_FREE_CHATS) { setGuestStep(3); return; }

    const effectiveCredits =
      credits !== null && credits !== undefined
        ? credits
        : user?.credits !== null && user?.credits !== undefined
        ? user.credits
        : null;

    if (user && isReal && effectiveCredits !== null && effectiveCredits <= 0) {
      setCreditMsg(
        language === "mr"
          ? "तुमचे दैनंदिन मोफत क्रेडिट्स संपले आहेत. पुढील संभाषणासाठी कृपया तुमचा प्लॅन अपग्रेड करा."
          : "Your daily credits have run out. Please upgrade your plan to continue chatting without limits."
      );
      setGuestStep(4);
      return;
    }

    let chatId = activeChatId;
    if (!chatId) { chatId = "temp-" + Date.now(); dispatch(setActiveChatId(chatId)); }

    if (truncateIndex !== undefined && truncateIndex !== null) {
      const truncated = messages.slice(0, truncateIndex);
      dispatch(setMessages({ chatId, messages: truncated }));
    }

    dispatch(addMessage({ chatId, message: { role: "user", content: userText } }));
    setInput("");

    if (isGenericMessage(userText)) {
      setGenericLoad(true);
      const targetLang = resolveMessageLanguage(userText, language);
      const replies = isThanks(userText)
        ? (THANKS_REPLIES_BY_LANG[targetLang] || THANKS_REPLIES_BY_LANG.en)
        : (GENERIC_REPLIES_BY_LANG[targetLang] || GENERIC_REPLIES_BY_LANG.en);
      const reply = randomPick(replies);
      await new Promise((r) => setTimeout(r, 900));
      setGenericLoad(false);
      dispatch(addMessage({ chatId, message: { role: "assistant", content: reply } }));
      return;
    }

    if (isGarbageText(userText)) {
      const targetLang = resolveMessageLanguage(userText, language);
      const garbageMsg = GARBAGE_REPLY_BY_LANG[targetLang] || GARBAGE_REPLY_BY_LANG.en;
      dispatch(addMessage({ chatId, message: { role: "assistant", content: garbageMsg } }));
      return;
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      dispatch(setLoading({ chatId, value: true }));

      // ── Pass state + department to backend ───────────────────────────────
      const res = await API.post(
        "/chat/ai",
        {
          message: userText,
          chatId: chatId.startsWith("temp-") ? null : chatId,
          language,
          state: selectedState,
          department: selectedDepartmentLabel,
          department_code: selectedDept === "ALL" ? "" : selectedDept,
          truncateIndex: truncateIndex,
        },
        {
          signal: controller.signal,
          timeout: 300000, // Important: Override the 10s default timeout (5 minutes for initial cold load)
        }
      );
      // ─────────────────────────────────────────────────────────────────────

      const botReply = res.data.response || "उत्तर उपलब्ध नाही.";
      const sources = res.data.sources || [];
      const newChatId = res.data.chatId || null;
      const limitReached = res.data.limitReached || false;
      const updatedCredits = res.data.credits;

      if (res.data.nextResetAt) setNextResetAt(res.data.nextResetAt);
      if (res.data.lastDailyCreditReset) setLastResetAt(res.data.lastDailyCreditReset);
      if (updatedCredits !== null && updatedCredits !== undefined) {
        dispatch(setCredits(updatedCredits));
        if (user && updatedCredits === 0) {
          setCreditMsg(
            language === "mr"
              ? "तुमचे आजचे सर्व मोफत क्रेडिट्स संपले आहेत. अखंडित सेवेसाठी कृपया तुमचा प्लॅन अपग्रेड करा."
              : "You've used all of your daily credits. Upgrade your plan to get more credits or wait for your daily renewal."
          );
          setGuestStep(4);
        }
      }

      if (chatId.startsWith("temp-") && newChatId) {
        const oldTempId = chatId;
        dispatch(migrateChatId({ oldId: oldTempId, newId: newChatId }));
        chatId = newChatId;
        if (activeChatId === oldTempId) dispatch(setActiveChatId(newChatId));
        dispatch(addChatToList({
          _id: newChatId, title: res.data.title || userText.substring(0, 40), messages: [],
          user: user?._id, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
        }));
      }

      if (limitReached) {
        if (!user) {
          setGuestStep(3);
        } else {
          setCreditMsg(
            botReply ||
              (language === "mr"
                ? "तुमचे दैनंदिन क्रेडिट्स संपले आहेत. कृपया तुमचा प्लॅन अपग्रेड करा."
                : "Your daily credits have run out. Please upgrade your plan.")
          );
          setGuestStep(4);
        }
        return;
      }

      dispatch(addMessage({
        chatId,
        message: {
          role: "assistant", content: botReply,
          sources: Array.isArray(sources) && sources.length > 0 ? sources : undefined,
        },
      }));

      if (!user) {
        setGuestChatCount((prev) => {
          const next = prev + 1;
          localStorage.setItem("grgpt_guest_chats", next.toString());
          if (next >= GUEST_FREE_CHATS) setGuestStep(3);
          return next;
        });
      }
    } catch (error) {
      if (axios.isCancel(error) || error.name === "CanceledError" || error.name === "AbortError") {
        console.log("Chat generation stopped by user.");
      } else {
        console.error("Chat Error:", error);
        const errorMsg = error.response?.data?.response || "❌ सर्व्हरशी संपर्क शक्य नाही.";
        dispatch(addMessage({ chatId, message: { role: "assistant", content: errorMsg } }));
      }
    } finally {
      abortControllerRef.current = null;
      dispatch(setLoading({ chatId, value: false }));
    }
  };

  const isEmpty = messages.length === 0 && !loading && !genericLoad;

  return (
    <div className={`chat-split-container ${activeSources ? "split-open" : ""}`}>
      <div className="chat-main-column">
        <div className="chat-container">

          {/* ── Top Bar ── */}
          <div className="gov-top-bar">
            <button
              className="mobile-menu-btn"
              onClick={() => dispatch(toggleSidebarCollapsed())}
              aria-label="Toggle Sidebar"
              type="button"
            >
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="12" x2="21" y2="12"></line>
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <line x1="3" y1="18" x2="21" y2="18"></line>
              </svg>
            </button>
            <div className="top-bar-actions">
              <LanguageSelector value={language} onChange={setLanguage} />
              <StateSelector value={selectedState} onChange={setSelectedState} language={language} states={states} onOpen={loadStates} />
              {user?.role === "admin" && (
                <button className="admin-return-btn" onClick={() => navigate("/admin")} type="button">
                  ← वापस मेनू
                </button>
              )}
            </div>
          </div>

          <div className="top-bar-spacer" />

          {/* ── Main Scrollable Area (Messages only when active) ── */}
          <div className={`chat-scroll-area ${isEmpty ? "chat-scroll-area--empty" : "chat-scroll-area--active"}`}>
            {/* Messages */}
            {!isEmpty && (
              <div className="chat-messages">
                {messages.map((msg, i) => {
                  let retryHandler = null;
                  if (msg.role === "user") {
                    retryHandler = () => sendMessage(null, msg.content, i);
                  } else if (msg.role === "assistant") {
                    retryHandler = () => handleRegenerateResponse(i);
                  }

                  const isEditingThisMsg = editingMsgIndex === i && msg.role === "user";

                  return (
                    <div key={msg.id || i} className={`message-row ${msg.role}`}>
                      {msg.role === "assistant" && (
                        <div className={`assistant-avatar assistant-avatar--static ${regeneratingMsgIndex === i ? "is-regenerating" : ""}`}>
                          <img src="/grgpt-emblem.png" alt="GRGPT" className="assistant-logo-img" />
                        </div>
                      )}
                      <div className={`message-content-wrap ${msg.role} ${isEditingThisMsg ? "editing" : ""}`}>
                        <div className={`message-bubble ${msg.role} ${isEditingThisMsg ? "editing" : ""}`}>
                          {isEditingThisMsg ? (
                            <div className="inline-edit-wrap">
                              <textarea
                                className="inline-edit-textarea"
                                ref={(el) => {
                                  if (el) {
                                    el.style.height = "auto";
                                    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
                                  }
                                }}
                                value={editText}
                                onChange={(e) => {
                                  setEditText(e.target.value);
                                  e.target.style.height = "auto";
                                  e.target.style.height = `${Math.min(e.target.scrollHeight, 200)}px`;
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    if (editText.trim() && editText.length <= MAX_CHAT_INPUT_LENGTH) {
                                      setEditingMsgIndex(null);
                                      sendMessage(null, editText.trim());
                                    }
                                  }
                                }}
                                maxLength={MAX_CHAT_INPUT_LENGTH}
                                rows={2}
                                autoFocus
                              />
                              <div className="inline-edit-actions">
                                {editText.length >= 1000 && (
                                  <span
                                    className={`char-counter ${editText.length >= MAX_CHAT_INPUT_LENGTH
                                        ? "limit-reached"
                                        : editText.length >= 1350
                                          ? "near-limit"
                                          : ""
                                      }`}
                                    title={`Character limit: ${editText.length}/${MAX_CHAT_INPUT_LENGTH}`}
                                  >
                                    {editText.length}/{MAX_CHAT_INPUT_LENGTH}
                                  </span>
                                )}
                                <button
                                  type="button"
                                  className="inline-edit-btn cancel"
                                  onClick={() => setEditingMsgIndex(null)}
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  className="inline-edit-btn save"
                                  onClick={() => {
                                    if (editText.trim() && editText.length <= MAX_CHAT_INPUT_LENGTH) {
                                      setEditingMsgIndex(null);
                                      sendMessage(null, editText.trim());
                                    }
                                  }}
                                >
                                  Send
                                </button>
                              </div>
                            </div>
                          ) : (
                            <>
                              {!msg.type && (
                                <div className="msg-text">
                                  <MarkdownRenderer text={msg.content} />
                                </div>
                              )}
                              {!msg.type && msg.sources?.length > 0 && (
                                <div className="msg-sources-pill-wrap">
                                  <SourcePillButton
                                    sources={msg.sources}
                                    isActive={activeSources?.msgIndex === i}
                                    onClick={() => handleToggleSources(i, msg)}
                                  />
                                </div>
                              )}
                            </>
                          )}
                        </div>
                        {!isEditingThisMsg && (
                          <MessageActions
                            msg={msg}
                            msgIndex={i}
                            onRetry={retryHandler}
                            onEdit={msg.role === "user" ? () => handleEditPrompt(i, msg.content) : null}
                            onVersionChange={handleVersionChange}
                            onShare={() => handleOpenShare(msg, i)}
                            isRegenerating={regeneratingMsgIndex === i}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}

                {loading && (
                  <div className="message-row assistant">
                    <AiThinkingSpinner />
                    <div className="message-bubble assistant thinking-bubble">
                      <ThinkingIndicator isGeneric={false} language={language} />
                    </div>
                  </div>
                )}
                {genericLoad && (
                  <div className="message-row assistant">
                    <AiThinkingSpinner />
                    <div className="message-bubble assistant thinking-bubble">
                      <ThinkingIndicator isGeneric={true} language={language} />
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>
            )}
          </div>

          {/* ── Persistent Hero & Input Section (Unified Gemini-style Layout) ── */}
          <div className={`chat-bottom-section ${isEmpty ? "chat-bottom-section--empty" : "chat-bottom-section--active"}`}>

            {/* Top spacer */}
            <div className={`chat-hero-top-spacer ${isEmpty ? "chat-hero-top-spacer--expanded" : ""}`} />

            {/* Hero Greeting sits directly above input box with 24px spacing */}
            <div className={`chat-hero-greeting ${isEmpty ? "chat-hero-greeting--visible" : "chat-hero-greeting--collapsed"}`}>
              <div className="chat-greeting-header">
                <h2 className="chat-greeting-title">
                  {getTimeBasedGreeting(transliteratedUserName || user?.name, language)}
                </h2>
              </div>
            </div>

            {/* Single Persistent Input Bar */}
            <ChatInputBar
              sendMessage={sendMessage}
              language={language}
              selectedDept={selectedDept}
              setSelectedDept={setSelectedDept}
              placeholder={placeholder}
              input={input}
              setInput={setInput}
              handleKeyDown={handleKeyDown}
              loading={loading}
              genericLoad={genericLoad}
              handleStopGeneration={handleStopGeneration}
              textareaRef={textareaRef}
            />

            {/* Suggestions chips right below the prompt bar */}
            <div className={`chat-suggestions-container ${isEmpty ? "chat-suggestions-container--visible" : "chat-suggestions-container--collapsed"}`}>
              <div className="chat-suggestions">
                {suggestions.map((s, i) => (
                  <button key={i} type="button" className="chat-suggestion-chip"
                    onClick={() => sendMessage(null, s)}>{s}</button>
                ))}
              </div>
            </div>

            {/* Disclaimer message at bottom */}
            <p className="chat-bottom-disclaimer">Grgpt is AI and can make mistakes.</p>

            {/* Bottom spacer for centering in empty state */}
            <div className={`chat-hero-bottom-spacer ${isEmpty ? "chat-hero-bottom-spacer--expanded" : ""}`} />
          </div>

          {/* ── Popups ── */}
          {showNotice && (
            <SystemNoticePopup
              message={systemNotice}
              onClose={() => {
                setShowNotice(false);
                sessionStorage.setItem("system_notice_dismissed", "1");
              }}
            />
          )}
          {!user && guestStep === 1 && <GuestAuthPopup onClose={handleAuthPopupClose} onNavigate={handlePopupNavigate} />}
          {!user && guestStep === 2 && <WelcomeCard onClose={handleWelcomeCardClose} freeChatsLeft={GUEST_FREE_CHATS - guestChatCount} />}
          {!user && guestStep === 3 && <GuestLimitPopup onClose={handleLimitPopupClose} onNavigate={handlePopupNavigate} />}
          {guestStep === 4 && (
            <CreditsExhaustedPopup
              message={creditMsg}
              onClose={handleCreditPopupClose}
              onNavigate={handlePopupNavigate}
              nextResetAt={nextResetAt || user?.nextResetAt}
              lastDailyCreditReset={lastResetAt || user?.lastDailyCreditReset}
            />
          )}
          {shareModalData && (
            <ShareModal
              isOpen={!!shareModalData}
              onClose={() => setShareModalData(null)}
              data={shareModalData}
            />
          )}
        </div>
      </div>

      {/* ── Right-Side Sources Split-View Sidebar (Grok Style) ── */}
      <SourcesSidebar
        isOpen={Boolean(activeSources)}
        onClose={() => setActiveSources(null)}
        sources={activeSources?.sources || []}
        query={activeSources?.query || ""}
        language={language}
      />
    </div>
  );
}
